import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBold, faCode, faListUl, faUnderline } from '@fortawesome/free-solid-svg-icons'
import { useLocation, useNavigate } from 'react-router-dom'
import ProgressBar from 'react-bootstrap/ProgressBar'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { db } from '../firebase'
import { buildCompetencyLedger } from './competencyLedger'
import StudentPerformanceComp from './StudentPerformanceComp'

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday']

const emptySubmissionForm = {
  responseItems: [],
  responseItemsHtml: [],
  responseText: '',
  responseTextHtml: '',
  githubLink: '',
  projectLink: '',
  demoVideoLink: '',
  notes: '',
  notesHtml: ''
}

const assignmentUsesPromptItems = (assignmentType) => (
  assignmentType === 'questions_text' || assignmentType === 'questions_code'
)

const assignmentExpectsCodeResponse = (assignmentType) => (
  assignmentType === 'questions_code' || assignmentType === 'code_paste'
)

const assignmentAllowsGithubLink = (assignmentType) => (
  assignmentType === 'code' || assignmentType === 'code_link'
)

const assignmentAllowsProjectLink = (assignmentType) => (
  assignmentType === 'project' || assignmentType === 'project_demo'
)

const assignmentAllowsDemoVideo = (assignmentType) => (
  assignmentType === 'project' || assignmentType === 'project_demo'
)

const normalizeText = (value) => String(value || '').trim()

const formatFileSize = (value) => {
  const size = Number(value) || 0
  if (size <= 0) return ''
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

const getAssignmentAttachment = (assignment = {}) => {
  const attachment = assignment?.assignmentAttachment
  if (!attachment || !normalizeText(attachment.url || attachment.downloadURL)) return null

  return {
    name: normalizeText(attachment.name) || 'Assignment file',
    url: normalizeText(attachment.url || attachment.downloadURL),
    size: Number(attachment.size) || 0,
    contentType: normalizeText(attachment.contentType || attachment.type)
  }
}

const sanitizeRichTextHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getPlainTextFromHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
}

const escapeHtml = (input) => String(input || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const createEditorHtmlFromPlainText = (input = '', preferCode = false) => {
  const raw = String(input || '')
  if (!raw.trim()) return ''

  if (preferCode) {
    return `<pre><code>${escapeHtml(raw)}</code></pre>`
  }

  return raw
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

const finalizeStudentResponseHtml = (input = '', preferCode = false) => {
  const cleanHtml = sanitizeRichTextHtml(input)
  const plainText = getPlainTextFromHtml(cleanHtml)

  if (!plainText) return ''

  if (preferCode && !/<(pre|code)\b/i.test(cleanHtml)) {
    return `<pre><code>${escapeHtml(plainText)}</code></pre>`
  }

  return cleanHtml
}

const normalizeCourseKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  return new Date(value).getTime() || 0
}

const formatCalendarDate = (value) => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return 'To be shared'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: typeof value === 'string' && value.length <= 10 ? undefined : 'short'
  }).format(new Date(timestamp))
}

const chunkArray = (items = [], size = 10) => {
  const result = []
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size))
  }
  return result
}

const normalizeDailyPlan = (days = []) => {
  if (!Array.isArray(days)) return []

  return days.map((day, dayIndex) => {
    if (typeof day === 'string') {
      return {
        id: `day-${dayIndex + 1}`,
        day: `Day ${dayIndex + 1}`,
        label: weekdays[dayIndex] || '',
        focus: day,
        teachingAid: '',
        reference: ''
      }
    }

    return {
      id: day.id || `day-${dayIndex + 1}`,
      day: day.day || `Day ${dayIndex + 1}`,
      label: day.label || day.weekday || day.date || weekdays[dayIndex] || '',
      focus: day.focus || day.topic || day.activities || day.description || 'Daily focus will be shared.',
      teachingAid: day.teachingAid || '',
      reference: day.reference || ''
    }
  })
}

const normalizeScheme = (value) => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `week-${index + 1}-${item}`,
          week: String(index + 1),
          topic: item,
          objectives: 'Objectives will be shared.',
          activities: 'Learning activities will be shared.',
          teachingAid: 'Teaching aids will be shared.',
          reference: 'References will be shared.',
          dailyPlan: []
        }
      }

      return {
        id: item.id || `week-${index + 1}-${item.topic || `Week ${index + 1}`}`,
        week: item.week || String(index + 1),
        topic: item.topic || `Week ${index + 1}`,
        objectives: item.objectives || item.objective || 'Objectives will be shared.',
        activities: item.activities || item.learningActivities || 'Learning activities will be shared.',
        teachingAid: item.teachingAid || item.aid || 'Teaching aids will be shared.',
        reference: item.reference || 'References will be shared.',
        dailyPlan: normalizeDailyPlan(item.dailyPlan || item.days || item.weekDays || item.dailyBreakdown)
      }
    })
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => ({
        id: `week-${index + 1}-${item}`,
        week: String(index + 1),
        topic: item,
        objectives: 'Objectives will be shared.',
        activities: 'Learning activities will be shared.',
        teachingAid: 'Teaching aids will be shared.',
        reference: 'References will be shared.',
        dailyPlan: []
      }))
  }

  return []
}

const normalizeTextList = (value) => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        return normalizeText(item?.text || item?.title || item?.description || item?.body)
      })
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n|•|;/)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  return []
}

const normalizeCollection = (value, fallbackId = 'item') => {
  if (!value) return []
  if (!Array.isArray(value)) return []

  return value
    .map((item, index) => {
      if (typeof item === 'string') {
        return { id: `${fallbackId}-${index + 1}`, title: item }
      }
      return { id: item.id || `${fallbackId}-${index + 1}`, ...item }
    })
    .filter((item) => item.title || item.name || item.body || item.note)
}

const resolveCourseField = (data, keys) => {
  for (const key of keys) {
    if (data?.[key]) return data[key]
  }
  return null
}

const getCourseTitle = (data = {}, fallback = '') => (
  data.mcourseName || data.courseName || data.name || data.title || fallback
)

const buildProgressDocId = (studentId, activeCourseName) => (
  `${studentId}_${String(activeCourseName || '').replace(/\//g, '__')}`
)

const buildSubmissionDocId = (assignmentId, studentId) => (
  `${String(assignmentId || '').replace(/\//g, '__')}__${String(studentId || '').replace(/\//g, '__')}`
)

const getWeekCompletionId = (week = {}, weekIndex = 0) => (
  week.id || week.topic || `week-${weekIndex + 1}`
)

const getWeekProgressUnits = (week = {}, weekIndex = 0) => {
  const weekId = getWeekCompletionId(week, weekIndex)
  const dailyPlan = Array.isArray(week.dailyPlan) ? week.dailyPlan : []

  if (!dailyPlan.length) {
    return [{
      key: `${weekId}::week`,
      day: 'Week',
      label: week.topic || `Week ${week.week || weekIndex + 1}`,
      focus: week.objectives || week.activities || 'Weekly work',
      teachingAid: week.teachingAid || '',
      reference: week.reference || ''
    }]
  }

  return dailyPlan.map((day, dayIndex) => ({
    ...day,
    key: `${weekId}::${normalizeText(day.id || day.day || `day-${dayIndex + 1}`) || `day-${dayIndex + 1}`}`
  }))
}

const expandCompletedDayItems = (progress = {}, scheme = []) => {
  const daySet = new Set(Array.isArray(progress.completedDayItems) ? progress.completedDayItems : [])
  const completedWeekIds = Array.isArray(progress.completedItems) ? progress.completedItems : []

  scheme.forEach((week, weekIndex) => {
    const weekId = getWeekCompletionId(week, weekIndex)
    if (!completedWeekIds.includes(weekId)) return
    getWeekProgressUnits(week, weekIndex).forEach((unit) => daySet.add(unit.key))
  })

  return daySet
}

const getProgressPercent = (progress = {}, scheme = []) => {
  const units = scheme.flatMap((week, weekIndex) => getWeekProgressUnits(week, weekIndex))
  if (!units.length) return 0

  const daySet = expandCompletedDayItems(progress, scheme)
  const completedUnitCount = units.filter((unit) => daySet.has(unit.key)).length
  return Math.round((completedUnitCount / units.length) * 100)
}

const hasSavedCompletedAssessment = (assessment) => Boolean(
  assessment?.status === 'completed' && assessment?.assessment
)

const getAssignmentStatusLabel = (submission, assessment = null) => (
  hasSavedCompletedAssessment(assessment)
    ? 'reviewed'
    : (submission?.reviewStatus || submission?.status || 'Pending')
)

const getAssignmentTypeLabel = (assignment) => {
  const type = assignment?.assignmentType
  if (assignment?.assignmentTypeLabel) return assignment.assignmentTypeLabel
  if (type === 'questions_text') return 'Questions to answer non code'
  if (type === 'questions_code') return 'Question to answer code submission'
  if (type === 'code_link' || type === 'code') return 'Code submission - GitHub link'
  if (type === 'code_paste') return 'Code submission - paste code'
  if (type === 'project_demo' || type === 'project') return 'Project link or demo'
  if (type === 'questions') return 'Questions to answer'
  if (type === 'general') return 'General task / reflection'
  return type || 'task'
}

const getAssignmentWeekLabel = (assignment = {}) => (
  assignment.assignmentWeekLabel
  || [
    assignment.assignmentWeek ? `Week ${assignment.assignmentWeek}` : '',
    assignment.assignmentWeekTopic || ''
  ].filter(Boolean).join(' • ')
)

const getAssignmentStatusKey = (submission, assessment = null) => {
  if (hasSavedCompletedAssessment(assessment)) return 'reviewed'
  if (!submission) return 'pending'

  const status = normalizeCourseKey(submission.reviewStatus || submission.status || 'submitted')
  if (status.includes('revision') || status.includes('redo')) return 'revision'
  if (status.includes('review')) return 'reviewed'
  if (status.includes('resubmitted')) return 'resubmitted'
  return 'submitted'
}

const getSubmissionLinks = (submission) => (
  [
    {
      label: 'GitHub repository',
      value: submission?.githubLink || submission?.githubUrl || submission?.repositoryUrl || submission?.repoLink
    },
    {
      label: 'Project link',
      value: submission?.projectLink || submission?.projectUrl || submission?.liveLink || submission?.liveUrl
    },
    {
      label: 'Demo video',
      value: submission?.videoLink || submission?.videoUrl || submission?.demoVideoLink || submission?.demoUrl
    }
  ].filter((item) => normalizeText(item.value))
)

const getProfileToneClass = (value = '', prefix = 'learningProfileTone') => {
  const signal = normalizeCourseKey(value)
  if (signal.includes('high') || signal.includes('ready') || signal.includes('strong')) return `${prefix}High`
  if (signal.includes('medium') || signal.includes('approaching') || signal.includes('steady')) return `${prefix}Medium`
  if (signal.includes('low') || signal.includes('needs') || signal.includes('risk')) return `${prefix}Low`
  return ''
}

const getLearningModeFitScore = (value) => {
  const numericValue = Number(value)
  if (!Number.isNaN(numericValue) && numericValue > 0) {
    return Math.max(0, Math.min(100, numericValue))
  }

  const signal = normalizeCourseKey(value)
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('strong') || signal.includes('best')) return 88
  if (signal.includes('medium') || signal.includes('moderate') || signal.includes('helpful')) return 64
  if (signal.includes('low') || signal.includes('limited') || signal.includes('weak')) return 36
  return 50
}

const formatProfileDate = (value) => {
  const timestamp = normalizeTimestamp(value) || Number(value) || 0
  if (!timestamp) return 'Not available'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(timestamp))
}

const getProfileSectionItems = (items = [], limit = 4) => (
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') {
        return { label: item, evidence: '', action: '' }
      }

      return {
        label: normalizeText(item?.label || item?.title || item?.theme || item?.mode),
        evidence: normalizeText(item?.evidence || item?.reason || item?.description || item?.whyItMatters),
        action: normalizeText(item?.action || item?.nextStep || item?.guidance || item?.firstMove)
      }
    })
    .filter((item) => item.label || item.evidence || item.action)
    .slice(0, limit)
)

const LEARNING_PROFILE_API_BASE = (
  process.env.REACT_APP_LEARNING_PROFILE_API_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/learningProfilesApi'
).replace(/\/$/, '')

const getInitialSubmissionForm = (assignment, submission) => {
  const promptItems = Array.isArray(assignment?.promptItems) ? assignment.promptItems : []
  const savedResponseItems = Array.isArray(submission?.responseItems)
    ? submission.responseItems.map((item) => normalizeText(item))
    : []
  const savedResponseItemsHtml = Array.isArray(submission?.responseItemsHtml)
    ? submission.responseItemsHtml.map((item, index) => (
      sanitizeRichTextHtml(item) || createEditorHtmlFromPlainText(savedResponseItems[index] || '', assignmentExpectsCodeResponse(assignment?.assignmentType))
    ))
    : []

  const responseItems = promptItems.map((_, index) => savedResponseItems[index] || '')
  const responseItemsHtml = promptItems.map((_, index) => (
    savedResponseItemsHtml[index] || createEditorHtmlFromPlainText(savedResponseItems[index] || '', assignmentExpectsCodeResponse(assignment?.assignmentType))
  ))

  return {
    responseItems,
    responseItemsHtml,
    responseText: submission?.responseText || submission?.answerText || '',
    responseTextHtml: sanitizeRichTextHtml(submission?.responseTextHtml || '') || createEditorHtmlFromPlainText(
      submission?.responseText || submission?.answerText || '',
      assignmentExpectsCodeResponse(assignment?.assignmentType)
    ),
    githubLink: submission?.githubLink || submission?.githubUrl || submission?.repositoryUrl || submission?.repoLink || '',
    projectLink: submission?.projectLink || submission?.projectUrl || submission?.liveLink || submission?.liveUrl || '',
    demoVideoLink: submission?.demoVideoLink || submission?.demoUrl || submission?.videoLink || submission?.videoUrl || '',
    notes: submission?.notes || submission?.comment || '',
    notesHtml: sanitizeRichTextHtml(submission?.notesHtml || '') || createEditorHtmlFromPlainText(submission?.notes || submission?.comment || '')
  }
}

const getSubmissionPrimaryLabel = (assignmentType) => {
  if (assignmentType === 'questions' || assignmentType === 'questions_text') return 'Answers'
  if (assignmentType === 'questions_code' || assignmentType === 'code_paste') return 'Paste code'
  if (assignmentType === 'project' || assignmentType === 'project_demo') return 'Project summary'
  return 'Work summary'
}

const getSubmissionPrimaryPlaceholder = (assignmentType) => {
  if (assignmentType === 'questions' || assignmentType === 'questions_text') {
    return 'Write your answers, explanations, or reflections here.'
  }
  if (assignmentType === 'questions_code' || assignmentType === 'code_paste') {
    return 'Paste your code response here.'
  }
  if (assignmentType === 'project' || assignmentType === 'project_demo') {
    return 'Describe what you built, what works, and what still needs improvement.'
  }
  return 'Explain your solution, what you built, and anything your instructor should review.'
}

const getAssignmentDisplayHtml = (assignment = {}) => {
  const instructionsHtml = sanitizeRichTextHtml(assignment.instructionsHtml || assignment.instructions || '')
  if (instructionsHtml) return instructionsHtml

  if (Array.isArray(assignment.promptItems) && assignment.promptItems.length > 0) {
    return assignment.promptItems
      .map((prompt, index) => (
        `<div class="studentAssignmentPromptDisplay"><span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}</div>`
      ))
      .join('')
  }

  return '<p>No instructions were added for this assignment yet.</p>'
}

const normalizeAssessmentList = (value) => (
  Array.isArray(value)
    ? value.filter((item) => {
        if (typeof item === 'string') return normalizeText(item)
        if (!item || typeof item !== 'object') return false
        return Object.values(item).some((field) => normalizeText(field))
      })
    : []
)

const formatAssessmentNumber = (value, fallback = '0') => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(1)
}

const getAssessmentScorePercent = (score, outOf = 5) => {
  const numericScore = Number(score)
  const numericOutOf = Number(outOf)
  if (!Number.isFinite(numericScore) || !Number.isFinite(numericOutOf) || numericOutOf <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((numericScore / numericOutOf) * 100)))
}

const renderAssessmentScore = (score, outOf = 5) => (
  `${formatAssessmentNumber(score)} / ${formatAssessmentNumber(outOf, '5')}`
)

const getAssessmentChartItems = (rubric = []) => (
  normalizeAssessmentList(rubric).map((item, index) => {
    const label = normalizeText(item?.label) || `Skill ${index + 1}`
    const shortLabel = label
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('') || String(index + 1)
    const outOf = Number(item?.outOf) || 5
    const score = Number(item?.score) || 0

    return {
      id: item?.id || `${label}-${index}`,
      label,
      shortLabel,
      outOf,
      percent: getAssessmentScorePercent(score, outOf),
      score
    }
  })
)

const getAssessmentListText = (item) => {
  if (typeof item === 'string') return normalizeText(item)
  if (!item || typeof item !== 'object') return ''
  return normalizeText(
    item.label || item.title || item.name || item.text || item.description || item.summary || item.headline || item.body
  )
}

const addInsightCounts = (bucket, items = []) => {
  normalizeAssessmentList(items).forEach((item) => {
    const text = getAssessmentListText(item)
    if (!text) return

    const key = normalizeCourseKey(text)
    const previous = bucket.get(key)

    bucket.set(key, {
      text: previous?.text || text,
      count: (previous?.count || 0) + 1
    })
  })
}

const rankInsightCounts = (bucket, limit = 4) => (
  Array.from(bucket.values())
    .sort((first, second) => second.count - first.count || first.text.localeCompare(second.text))
    .slice(0, limit)
)

const buildAssignmentReadDocId = (studentId, assignmentId) => (
  `${String(studentId || '').replace(/\//g, '__')}__${String(assignmentId || '').replace(/\//g, '__')}`
)

function CourseDetailsComp({ embedded = false, forcedCourseName = '', courseSummary = null }) {
  const [loading, setLoading] = useState(true)
  const [courseName, setCourseName] = useState('')
  const [courseDesc, setCourseDesc] = useState('')
  const [courseOverview, setCourseOverview] = useState([])
  const [courseDeliverables, setCourseDeliverables] = useState([])
  const [schemeOfWork, setSchemeOfWork] = useState([])
  const [assessments, setAssessments] = useState([])
  const [notes, setNotes] = useState([])
  const [courseProgress, setCourseProgress] = useState({ completedItems: [], completedDayItems: [] })
  const [userId, setUserId] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  const [loadMessage, setLoadMessage] = useState('')
  const [studentProfile, setStudentProfile] = useState(null)
  const [activeCourseData, setActiveCourseData] = useState(null)
  const [assignmentsList, setAssignmentsList] = useState([])
  const [assignmentSubmissions, setAssignmentSubmissions] = useState({})
  const [assessmentBySubmission, setAssessmentBySubmission] = useState({})
  const [assignmentLearningProfileDoc, setAssignmentLearningProfileDoc] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [profileRefreshLoading, setProfileRefreshLoading] = useState(false)
  const [profileRefreshMessage, setProfileRefreshMessage] = useState('')
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('')
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false)
  const [isAssignmentReviewOpen, setIsAssignmentReviewOpen] = useState(false)
  const [showAssessmentSubmissionEditor, setShowAssessmentSubmissionEditor] = useState(false)
  const [submissionForm, setSubmissionForm] = useState(emptySubmissionForm)
  const [submissionNotice, setSubmissionNotice] = useState('')
  const [savingSubmission, setSavingSubmission] = useState(false)
  const [assessmentLoading, setAssessmentLoading] = useState(false)
  const [assessmentError, setAssessmentError] = useState('')
  const primaryResponseEditorRef = useRef(null)
  const notesEditorRef = useRef(null)
  const promptResponseEditorRefs = useRef({})
  const handledLocationStateRef = useRef('')

  const auth = getAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const handleRefreshCourseDetails = useCallback(async () => {
    const currentUser = auth.currentUser
    const currentStudentId = currentUser?.uid || userId

    if (!currentUser || !currentStudentId) {
      setProfileRefreshMessage('Please sign in again to rebuild your learning profile.')
      return
    }

    setProfileRefreshLoading(true)
    setProfileRefreshMessage('')

    try {
      const idToken = await currentUser.getIdToken(true)
      const response = await fetch(`${LEARNING_PROFILE_API_BASE}/profiles/rebuild`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          studentId: currentStudentId,
          source: 'assignments',
          force: true
        })
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload?.error || 'The learning profile could not be rebuilt right now.')
      }

      setProfileRefreshMessage('Learning profile rebuilt. Loading the latest report...')
      setRefreshKey((current) => current + 1)
    } catch (error) {
      console.error('Error rebuilding assignment learning profile:', error)
      setProfileRefreshMessage(error?.message || 'We could not rebuild the learning profile right now.')
    } finally {
      setProfileRefreshLoading(false)
    }
  }, [auth, userId])

  const displayCourseName = courseName || 'Course Details'
  const displayCourseDesc = courseDesc || ''
  const displayScheme = schemeOfWork
  const displayOverview = courseOverview
  const displayDeliverables = courseDeliverables
  const displayAssessments = assessments
  const displayNotes = notes

  const schemeItems = useMemo(() => displayScheme.filter(Boolean), [displayScheme])
  const completedDaySet = useMemo(
    () => expandCompletedDayItems(courseProgress, schemeItems),
    [courseProgress, schemeItems]
  )
  const progressUnits = useMemo(
    () => schemeItems.flatMap((week, weekIndex) => getWeekProgressUnits(week, weekIndex)),
    [schemeItems]
  )
  const completedUnitCount = useMemo(
    () => progressUnits.filter((unit) => completedDaySet.has(unit.key)).length,
    [completedDaySet, progressUnits]
  )
  const progressPercent = useMemo(
    () => getProgressPercent(courseProgress, schemeItems),
    [courseProgress, schemeItems]
  )

  const assignments = useMemo(() => (
    [...assignmentsList].sort((first, second) => {
      const firstCreated = normalizeTimestamp(first.createdAtMs || first.createdAt || first.updatedAtMs || first.updatedAt)
      const secondCreated = normalizeTimestamp(second.createdAtMs || second.createdAt || second.updatedAtMs || second.updatedAt)
      return secondCreated - firstCreated
    })
  ), [assignmentsList])

  const assignmentStats = useMemo(() => {
    const stats = {
      total: assignments.length,
      pending: 0,
      submitted: 0,
      reviewed: 0,
      revision: 0
    }

    assignments.forEach((assignment) => {
      const submission = assignmentSubmissions[assignment.id]
      const statusKey = getAssignmentStatusKey(submission, submission?.id ? assessmentBySubmission[submission.id] : null)
      if (statusKey === 'pending') stats.pending += 1
      if (statusKey === 'submitted' || statusKey === 'resubmitted') stats.submitted += 1
      if (statusKey === 'reviewed') stats.reviewed += 1
      if (statusKey === 'revision') stats.revision += 1
    })

    return stats
  }, [assessmentBySubmission, assignmentSubmissions, assignments])

  const assignmentSummaryCards = useMemo(() => ([
    {
      key: 'pending',
      className: 'assessmentCardPending',
      label: 'Not submitted',
      value: assignmentStats.pending + assignmentStats.revision,
      note: assignmentStats.revision > 0
        ? `${assignmentStats.revision} ${assignmentStats.revision === 1 ? 'needs' : 'need'} another submission`
        : 'Assignments still waiting for your work'
    },
    {
      key: 'submitted',
      className: 'assessmentCardSubmitted',
      label: 'Submitted',
      value: assignmentStats.submitted,
      note: 'Work sent and currently awaiting review'
    },
    {
      key: 'reviewed',
      className: 'assessmentCardReviewed',
      label: 'Reviewed',
      value: assignmentStats.reviewed,
      note: 'Assignments with feedback ready to open'
    }
  ]), [assignmentStats.pending, assignmentStats.reviewed, assignmentStats.revision, assignmentStats.submitted])

  const reviewedAssignmentEntries = useMemo(() => (
    assignments
      .map((assignment) => {
        const submission = assignmentSubmissions[assignment.id]
        const assessment = submission?.id ? assessmentBySubmission[submission.id] : null

        if (!submission || !hasSavedCompletedAssessment(assessment)) return null

        return {
          assignment,
          submission,
          assessment,
          reviewedAt: normalizeTimestamp(
            assessment?.updatedAt
            || assessment?.completedAt
            || submission.updatedAt
            || submission.submittedAt
            || submission.createdAt
          )
        }
      })
      .filter(Boolean)
      .sort((first, second) => second.reviewedAt - first.reviewedAt)
  ), [assessmentBySubmission, assignmentSubmissions, assignments])

  const competencyCourseName = displayCourseName || 'Course Details'
  const ledgerAssignments = useMemo(() => (
    assignments.map((assignment) => {
      const submission = assignmentSubmissions[assignment.id] || null
      const assessment = submission?.id ? assessmentBySubmission[submission.id] || null : null

      return {
        ...assignment,
        assessment,
        latestSubmission: submission,
      }
    })
  ), [assessmentBySubmission, assignmentSubmissions, assignments])

  const competencyLedgerBundle = useMemo(() => buildCompetencyLedger({
    courseTabs: [competencyCourseName],
    assignmentsByCourse: {
      [competencyCourseName]: ledgerAssignments,
    },
    progressByCourse: {
      [competencyCourseName]: {
        scheme: schemeItems,
        progressData: courseProgress,
        completedUnitLabels: progressUnits
          .filter((unit) => completedDaySet.has(unit.key))
          .map((unit) => normalizeCourseKey(unit.label)),
        updatedAtMs: normalizeTimestamp(
          courseProgress.updatedAt
          || courseProgress.updatedAtMs
          || courseProgress.lastUpdatedAt
          || courseProgress.completedAt
        ),
      },
    },
    assignmentProfileData: assignmentLearningProfileDoc || {},
  }), [assignmentLearningProfileDoc, competencyCourseName, completedDaySet, courseProgress, ledgerAssignments, progressUnits, schemeItems])

  const competencyLedgerSummary = competencyLedgerBundle.summary || null
  const activeCompetencies = useMemo(
    () => competencyLedgerBundle.ledgerByCourse?.[competencyCourseName] || [],
    [competencyCourseName, competencyLedgerBundle]
  )
  const competencyStatusBreakdown = useMemo(() => ({
    advanced: activeCompetencies.filter((item) => item.status === 'advanced').length,
    proficient: activeCompetencies.filter((item) => item.status === 'proficient').length,
    developing: activeCompetencies.filter((item) => item.status === 'developing').length,
    emerging: activeCompetencies.filter((item) => item.status === 'emerging').length,
  }), [activeCompetencies])

  const topActiveCompetency = useMemo(() => (
    activeCompetencies.length
      ? [...activeCompetencies].sort((first, second) => (
          second.evidenceCount - first.evidenceCount || second.score - first.score
        ))[0]
      : null
  ), [activeCompetencies])

  const learningProfile = useMemo(() => {
    const latestEntry = reviewedAssignmentEntries[0] || null
    const rubricMap = new Map()
    const winsMap = new Map()
    const focusMap = new Map()

    let rubricPercentTotal = 0
    let rubricDimensionCount = 0

    reviewedAssignmentEntries.forEach(({ assessment }) => {
      const assessmentData = assessment?.assessment || {}

      normalizeAssessmentList(assessmentData.rubric).forEach((item, index) => {
        const label = normalizeText(item?.label) || `Skill ${index + 1}`
        const outOf = Number(item?.outOf) || 5
        const score = Number(item?.score) || 0
        const percent = getAssessmentScorePercent(score, outOf)
        const key = normalizeCourseKey(label)
        const previous = rubricMap.get(key)

        rubricPercentTotal += percent
        rubricDimensionCount += 1

        rubricMap.set(key, {
          label: previous?.label || label,
          count: (previous?.count || 0) + 1,
          totalPercent: (previous?.totalPercent || 0) + percent
        })
      })

      addInsightCounts(winsMap, assessmentData.strengths)
      addInsightCounts(winsMap, assessmentData.studentFeedback?.celebration)
      addInsightCounts(focusMap, assessmentData.growthAreas)
      addInsightCounts(focusMap, assessmentData.misconceptions)
      addInsightCounts(focusMap, assessmentData.studentFeedback?.improve)

      const nextChallenge = normalizeText(assessmentData.studentFeedback?.nextChallenge)
      if (nextChallenge) addInsightCounts(focusMap, [nextChallenge])
    })

    const rubricAverages = Array.from(rubricMap.values())
      .map((item) => ({
        ...item,
        averagePercent: item.count > 0 ? Math.round(item.totalPercent / item.count) : 0
      }))
      .sort((first, second) => second.averagePercent - first.averagePercent || second.count - first.count)

    const rubricFocusList = [...rubricAverages].sort((first, second) => first.averagePercent - second.averagePercent || second.count - first.count)
    const latestAssessment = latestEntry?.assessment?.assessment || {}

    return {
      latestEntry,
      reviewedCount: reviewedAssignmentEntries.length,
      averageRubricPercent: rubricDimensionCount > 0 ? Math.round(rubricPercentTotal / rubricDimensionCount) : 0,
      rubricDimensionCount,
      strongestRubric: rubricAverages[0] || null,
      focusRubric: rubricFocusList[0] || null,
      winSignals: rankInsightCounts(winsMap, 4),
      focusSignals: rankInsightCounts(focusMap, 4),
      recentReviews: reviewedAssignmentEntries.slice(0, 3).map(({ assignment, submission, assessment }) => {
        const review = assessment?.assessment || {}
        return {
          id: `${assignment.id}-${submission.id}`,
          title: assignment.title || 'Assignment',
          date: formatCalendarDate(assessment?.updatedAt || assessment?.completedAt || submission.updatedAt || submission.submittedAt || submission.createdAt),
          signal: review.summary?.headline || review.summary?.overallSignal || review.summary?.futureReadiness || 'Review ready',
          detail: review.studentFeedback?.nextChallenge
            || review.summary?.learnerSnapshot
            || review.summary?.overallAssessment
            || 'Detailed feedback is available inside this assignment review.'
        }
      }),
      latestSignal: latestAssessment.summary?.overallSignal || latestAssessment.summary?.futureReadiness || 'Your reviewed work is building a clear learning profile',
      latestHeadline: latestAssessment.summary?.headline || 'A course-wide view of your performance and progress',
      latestSummary: latestAssessment.summary?.learnerSnapshot
        || latestAssessment.summary?.futureOfLearningLens
        || latestAssessment.summary?.overallAssessment
        || 'Each reviewed assignment adds to your learning profile, showing what is becoming strong and what needs more practice.'
    }
  }, [reviewedAssignmentEntries])

  const generatedAssignmentLearningProfile = useMemo(() => {
    if (!assignmentLearningProfileDoc?.profile) return null

    const profile = assignmentLearningProfileDoc.profile || {}
    const analytics = assignmentLearningProfileDoc.analytics || {}
    const profileMeta = assignmentLearningProfileDoc.profileMeta || {}
    const learningModes = (Array.isArray(profile.profileSignals?.learningModeFit) ? profile.profileSignals.learningModeFit : [])
      .map((item) => ({
        mode: normalizeText(item?.mode),
        fit: normalizeText(item?.fit),
        evidence: normalizeText(item?.evidence),
        score: getLearningModeFitScore(item?.fit)
      }))
      .filter((item) => item.mode || item.fit || item.evidence)

    return {
      headline: profile.summary?.headline || 'Assignment learning profile',
      learnerSnapshot: profile.summary?.learnerSnapshot || profile.summary?.momentumNarrative || 'Your reviewed assignments are starting to reveal how you learn best.',
      futureOfLearningLens: profile.summary?.futureOfLearningLens || '',
      momentumNarrative: profile.summary?.momentumNarrative || '',
      confidence: profile.summary?.confidence || 'Building signal',
      confidenceReason: profile.summary?.confidenceReason || '',
      readinessBand: profile.profileSignals?.readinessBand || 'Building profile',
      evidenceStrength: profileMeta.evidenceStrength || 'low',
      evidenceSummary: profileMeta.summary || '',
      reviewCount: profileMeta.sourceCount || analytics.reviewCount || 0,
      latestEvidenceLabel: formatProfileDate(profileMeta.latestSourceAtMs || assignmentLearningProfileDoc.sourceMeta?.latestSourceAtMs),
      lastAnalyzedLabel: formatProfileDate(
        assignmentLearningProfileDoc.lastAnalyzedAt
        || assignmentLearningProfileDoc.lastAnalyzedAtMs
        || assignmentLearningProfileDoc.updatedAt
        || assignmentLearningProfileDoc.generatedAt
        || assignmentLearningProfileDoc.updatedAtMs
        || assignmentLearningProfileDoc.generatedAtMs
      ),
      rebuiltAtLabel: formatProfileDate(
        assignmentLearningProfileDoc.updatedAt
        || assignmentLearningProfileDoc.generatedAt
        || assignmentLearningProfileDoc.updatedAtMs
        || assignmentLearningProfileDoc.generatedAtMs
      ),
      learningModes,
      rubricAverages: (Array.isArray(analytics.rubricAverages) ? analytics.rubricAverages : []).slice(0, 6),
      averageRubricPercent: Number(analytics.averageRubricPercent) || 0,
      strengths: getProfileSectionItems(profile.performancePatterns?.strongestRecurringDimensions, 4),
      developing: getProfileSectionItems(profile.performancePatterns?.developingDimensions, 4),
      performancePatterns: {
        consistencyPattern: normalizeText(profile.performancePatterns?.consistencyPattern),
        revisionPattern: normalizeText(profile.performancePatterns?.revisionPattern),
        curriculumReadiness: normalizeText(profile.performancePatterns?.curriculumReadiness)
      },
      changeSinceLastUpdate: {
        summary: normalizeText(profile.changeSinceLastUpdate?.summary),
        newStrengths: normalizeTextList(profile.changeSinceLastUpdate?.newStrengths).slice(0, 4),
        newRisks: normalizeTextList(profile.changeSinceLastUpdate?.newRisks).slice(0, 4),
        improvedSignals: normalizeTextList(profile.changeSinceLastUpdate?.improvedSignals).slice(0, 4),
        declinedSignals: normalizeTextList(profile.changeSinceLastUpdate?.declinedSignals).slice(0, 4)
      },
      learningIdentity: {
        primaryModes: normalizeTextList(profile.learningIdentity?.primaryModes).slice(0, 4),
        strongestHabits: normalizeTextList(profile.learningIdentity?.strongestHabits).slice(0, 4),
        frictionPatterns: normalizeTextList(profile.learningIdentity?.frictionPatterns).slice(0, 4),
        paceProfile: normalizeText(profile.learningIdentity?.paceProfile),
        confidencePattern: normalizeText(profile.learningIdentity?.confidencePattern),
        transferPattern: normalizeText(profile.learningIdentity?.transferPattern)
      },
      diagnosticLens: {
        misconceptionRecurrence: getProfileSectionItems(profile.diagnosticLens?.misconceptionRecurrence, 4),
        persistence: {
          notUnderstoodYet: normalizeTextList(profile.diagnosticLens?.persistenceVsUnderstanding?.notUnderstoodYet).slice(0, 3),
          inconsistentExecution: normalizeTextList(profile.diagnosticLens?.persistenceVsUnderstanding?.inconsistentExecution).slice(0, 3),
          rushesOrSkipsChecks: normalizeTextList(profile.diagnosticLens?.persistenceVsUnderstanding?.rushesOrSkipsChecks).slice(0, 3),
          interpretation: normalizeText(profile.diagnosticLens?.persistenceVsUnderstanding?.evidenceBasedInterpretation)
        }
      },
      teacherGuidance: {
        interventionPriorities: (Array.isArray(profile.teacherGuidance?.interventionPriorities) ? profile.teacherGuidance.interventionPriorities : [])
          .slice(0, 3)
          .map((item, index) => ({
            rank: Number(item?.rank) || index + 1,
            label: normalizeText(item?.label || item?.title || item?.priority),
            whyItMatters: normalizeText(item?.whyItMatters || item?.reason || item?.evidence),
            firstMove: normalizeText(item?.firstMove || item?.action || item?.nextStep)
          })),
        nextLessonMoves: normalizeTextList(profile.teacherGuidance?.nextLessonMoves).slice(0, 4),
        thisWeekMoves: normalizeTextList(profile.teacherGuidance?.thisWeekMoves).slice(0, 4),
        nextAssessmentCycleMoves: normalizeTextList(profile.teacherGuidance?.nextAssessmentCycleMoves).slice(0, 4)
      },
      recentReviews: (Array.isArray(analytics.recentReviews) ? analytics.recentReviews : []).slice(0, 4).map((item, index) => ({
        id: item.reviewId || `review-${index + 1}`,
        title: normalizeText(item.assignmentTitle) || `Reviewed assignment ${index + 1}`,
        courseName: normalizeText(item.courseName),
        date: formatProfileDate(item.reviewedAtMs),
        headline: normalizeText(item.headline || item.overallSignal || item.futureReadiness || 'Review insight'),
        detail: normalizeText(item.learnerSnapshot || item.nextChallenge || (Array.isArray(item.strengths) ? item.strengths[0] : ''))
      })),
      studentFacing: {
        encouragement: normalizeText(profile.studentFacing?.encouragement),
        whatIsGoingWell: normalizeTextList(profile.studentFacing?.whatIsGoingWell).slice(0, 4),
        focusNext: normalizeTextList(profile.studentFacing?.focusNext).slice(0, 4),
        whyThisMatters: normalizeText(profile.studentFacing?.whyThisMatters),
        reflectionPrompt: normalizeText(profile.studentFacing?.reflectionPrompt)
      },
      fireflyRecommendation: {
        shouldInviteLiveSession: Boolean(profile.fireflyRecommendation?.shouldInviteLiveSession),
        urgency: normalizeText(profile.fireflyRecommendation?.urgency) || 'low',
        studentSummary: normalizeText(profile.fireflyRecommendation?.studentSummary),
        outreachReason: normalizeText(profile.fireflyRecommendation?.outreachReason || profile.fireflyRecommendation?.teacherReason),
        focusConcepts: getProfileSectionItems(profile.fireflyRecommendation?.recommendedFocusConcepts, 4),
        sessionGoals: normalizeTextList(profile.fireflyRecommendation?.sessionGoals).slice(0, 4),
        inviteWithin: normalizeText(profile.fireflyRecommendation?.inviteWithin),
        suggestedSessionLength: normalizeText(profile.fireflyRecommendation?.suggestedSessionLength)
      },
      actionPlan: {
        keepDoing: normalizeTextList(profile.studentActionPlan?.keepDoing).slice(0, 4),
        focusNow: normalizeTextList(profile.studentActionPlan?.focusNow).slice(0, 4),
        practiceRoutine: normalizeTextList(profile.studentActionPlan?.practiceRoutine).slice(0, 4),
        nextChallenge: normalizeText(profile.studentActionPlan?.nextChallenge),
        reflectionPrompt: normalizeText(profile.studentActionPlan?.reflectionPrompt)
      }
    }
  }, [assignmentLearningProfileDoc])

  const submissionAssessmentEntries = useMemo(
    () => Object.entries(assignmentSubmissions).filter(([, submission]) => submission?.id),
    [assignmentSubmissions]
  )
  const submissionAssessmentKey = useMemo(
    () => submissionAssessmentEntries.map(([, submission]) => submission.id).sort().join('|'),
    [submissionAssessmentEntries]
  )

  const selectedAssignment = useMemo(
    () => assignments.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [assignments, selectedAssignmentId]
  )
  const selectedSubmission = selectedAssignment ? assignmentSubmissions[selectedAssignment.id] || null : null
  const selectedAssessment = selectedSubmission?.id ? (assessmentBySubmission[selectedSubmission.id] || null) : null
  const selectedSubmissionStatusKey = getAssignmentStatusKey(selectedSubmission, selectedAssessment)
  const rubricChartItems = useMemo(
    () => getAssessmentChartItems(selectedAssessment?.assessment?.rubric),
    [selectedAssessment]
  )
  const hasCompletedAssessment = Boolean(
    selectedSubmissionStatusKey === 'reviewed' &&
    hasSavedCompletedAssessment(selectedAssessment)
  )

  const applyCourseData = (data = {}, fallbackName = '') => {
    const resolvedName = getCourseTitle(data, fallbackName)
    setCourseName(resolvedName || fallbackName)
    setCourseDesc(
      data.mcourseDescription ||
      data.courseDescription ||
      data.description ||
      data.about ||
      data.summary ||
      ''
    )
    setCourseOverview(
      normalizeTextList(resolveCourseField(data, ['overview', 'courseOverview', 'curriculumOverview', 'aboutProgram']))
    )
    setCourseDeliverables(
      normalizeTextList(resolveCourseField(data, ['deliverables', 'courseDeliverables', 'outcomes', 'learningOutcomes']))
    )
    setSchemeOfWork(
      normalizeScheme(resolveCourseField(data, ['schemeOfWork', 'scheme', 'courseScheme', 'schemeTemplate']))
    )
    setAssessments(
      normalizeCollection(resolveCourseField(data, ['assessments', 'assessmentPlan', 'courseAssessments']), 'assessment')
    )
    setNotes(
      normalizeCollection(resolveCourseField(data, ['notes', 'instructorNotes', 'courseNotes']), 'note')
    )
  }

  const findCourseByName = async (courseToFind) => {
    if (!courseToFind) return null

    const directQueries = [
      query(collection(db, 'courses'), where('mcourseName', '==', courseToFind)),
      query(collection(db, 'courses'), where('courseName', '==', courseToFind)),
      query(collection(db, 'courses'), where('name', '==', courseToFind))
    ]

    for (const courseQuery of directQueries) {
      const snapshot = await getDocs(courseQuery)
      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0]
        return { id: docSnap.id, ...docSnap.data() }
      }
    }

    const normalizedTarget = normalizeCourseKey(courseToFind)
    const allCoursesSnapshot = await getDocs(collection(db, 'courses'))
    const matchedDoc = allCoursesSnapshot.docs.find((docSnap) => {
      const data = docSnap.data()
      const names = [
        data.mcourseName,
        data.courseName,
        data.name,
        data.title,
        docSnap.id
      ]
      return names.some((name) => normalizeCourseKey(name) === normalizedTarget)
    })

    if (!matchedDoc) return null
    return { id: matchedDoc.id, ...matchedDoc.data() }
  }

  useEffect(() => {
    if (!embedded && location.state?.tab) {
      setActiveTab(location.state.tab)
    }
  }, [embedded, location.state?.tab])

  useEffect(() => {
    const openAssignmentId = location.state?.openAssignmentId
    if (!openAssignmentId) return

    const locationStateKey = `${location.key}:${openAssignmentId}`
    if (handledLocationStateRef.current === locationStateKey) return
    if (!assignments.some((assignment) => assignment.id === openAssignmentId)) return

    handledLocationStateRef.current = locationStateKey
    setActiveTab('assessments')
    setSelectedAssignmentId(openAssignmentId)
    setSubmissionNotice('')
    setAssessmentError('')
    setIsAssignmentReviewOpen(false)
    setIsAssessmentOpen(true)
  }, [assignments, location.key, location.state])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true)
      setLoadMessage('')
      setSubmissionNotice('')

      if (!user) {
        setAssignmentLearningProfileDoc(null)
        setLoadMessage('Please sign in to view your course details.')
        setLoading(false)
        return
      }

      const uid = user.uid
      setUserId(uid)

      try {
        let resolvedCourseName = forcedCourseName || location.state?.courseName || ''
        const userSnapshot = await getDocs(query(collection(db, 'users'), where('userId', '==', uid)))
        const userDoc = userSnapshot.docs[0]
        const userData = userDoc?.data() || {}
        setStudentProfile({ id: userDoc?.id || '', ...userData })

        const assignmentProfileSnap = await getDoc(doc(db, 'studentAssignmentLearningProfiles', uid))
        setAssignmentLearningProfileDoc(
          assignmentProfileSnap.exists()
            ? { id: assignmentProfileSnap.id, ...assignmentProfileSnap.data() }
            : null
        )

        if (!resolvedCourseName) {
          resolvedCourseName = userData.course || userData.courseName || userData.currentCourse || ''
        }

        if (!resolvedCourseName) {
          setCourseName('')
          setCourseDesc('')
          setCourseOverview([])
          setCourseDeliverables([])
          setSchemeOfWork([])
          setAssessments([])
          setNotes([])
          setAssignmentsList([])
          setAssignmentSubmissions({})
          setAssessmentBySubmission({})
          setCourseProgress({ completedItems: [], completedDayItems: [] })
          setLoadMessage('Your course has not been assigned yet. Once admin assigns your course, the full overview and scheme of work will appear here.')
          setLoading(false)
          return
        }

        setCourseName(resolvedCourseName)

        const courseData = await findCourseByName(resolvedCourseName)
        const resolvedCourseTitle = getCourseTitle(courseData || {}, resolvedCourseName)
        const normalizedCourseKeys = new Set([
          normalizeCourseKey(resolvedCourseName),
          normalizeCourseKey(resolvedCourseTitle),
          normalizeCourseKey(courseData?.courseKey),
          normalizeCourseKey(courseData?.id)
        ].filter(Boolean))

        if (courseData) {
          applyCourseData(courseData, resolvedCourseName)
          setActiveCourseData(courseData)
        } else {
          setCourseDesc('')
          setCourseOverview([])
          setCourseDeliverables([])
          setSchemeOfWork([])
          setAssessments([])
          setNotes([])
          setActiveCourseData(null)
          setLoadMessage('This course is assigned to your account, but admin has not published its course details yet.')
        }

        const progressDocIds = Array.from(new Set([
          buildProgressDocId(uid, resolvedCourseTitle),
          buildProgressDocId(uid, resolvedCourseName)
        ]))

        let resolvedProgress = { completedItems: [], completedDayItems: [] }
        for (const progressDocId of progressDocIds) {
          const progressSnap = await getDoc(doc(db, 'courseProgress', progressDocId))
          if (progressSnap.exists()) {
            resolvedProgress = { id: progressSnap.id, ...progressSnap.data() }
            break
          }
        }
        setCourseProgress({
          ...resolvedProgress,
          completedItems: Array.isArray(resolvedProgress.completedItems) ? resolvedProgress.completedItems : [],
          completedDayItems: Array.isArray(resolvedProgress.completedDayItems) ? resolvedProgress.completedDayItems : []
        })

        const assignmentsSnapshot = await getDocs(collection(db, 'courseAssignments'))
        const studentAssignments = assignmentsSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((assignment) => {
            const assignmentCourseKey = normalizeCourseKey(assignment.courseKey || assignment.courseName)
            const assignmentStudentIds = Array.isArray(assignment.studentIds)
              ? assignment.studentIds.map((studentId) => String(studentId))
              : []

            const matchesCourse = (
              assignment.courseId === courseData?.id ||
              normalizedCourseKeys.has(assignmentCourseKey) ||
              normalizedCourseKeys.has(normalizeCourseKey(assignment.courseName))
            )

            const matchesStudent = (
              normalizeCourseKey(assignment.audience) === 'all' ||
              assignmentStudentIds.length === 0 ||
              assignmentStudentIds.includes(uid)
            )

            return matchesCourse && matchesStudent
          })

        setAssignmentsList(studentAssignments)

        if (studentAssignments.length > 0) {
          const submissionsByAssignment = {}
          const assignmentChunks = chunkArray(studentAssignments.map((assignment) => assignment.id).filter(Boolean), 10)

          await Promise.all(assignmentChunks.map(async (assignmentChunk) => {
            const submissionSnapshot = await getDocs(
              query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk))
            )

            submissionSnapshot.forEach((docSnap) => {
              const submission = { id: docSnap.id, ...docSnap.data() }
              const submissionStudentId = submission.studentId || submission.studentID || submission.userId || submission.submittedById
              const submissionEmail = normalizeText(submission.studentEmail || submission.userEmail).toLowerCase()

              if (submissionStudentId !== uid && submissionEmail !== normalizeText(user.email).toLowerCase()) {
                return
              }

              const previousSubmission = submissionsByAssignment[submission.assignmentId]
              const previousTimestamp = normalizeTimestamp(previousSubmission?.updatedAt || previousSubmission?.submittedAt || previousSubmission?.createdAt)
              const nextTimestamp = normalizeTimestamp(submission.updatedAt || submission.submittedAt || submission.createdAt)

              if (!previousSubmission || nextTimestamp >= previousTimestamp) {
                submissionsByAssignment[submission.assignmentId] = submission
              }
            })
          }))

          setAssignmentSubmissions(submissionsByAssignment)
        } else {
          setAssignmentSubmissions({})
          setAssessmentBySubmission({})
        }
      } catch (error) {
        console.error('Error loading course details:', error)
        setLoadMessage('We could not load your course details right now. Please try again shortly.')
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth, forcedCourseName, location.state?.courseName, refreshKey])

  useEffect(() => {
    if (!assignments.length) {
      setSelectedAssignmentId('')
      setIsAssessmentOpen(false)
      return
    }

    if (!assignments.some((assignment) => assignment.id === selectedAssignmentId)) {
      setSelectedAssignmentId(assignments[0].id)
    }
  }, [assignments, selectedAssignmentId])

  useEffect(() => {
    setSubmissionForm(getInitialSubmissionForm(selectedAssignment, selectedSubmission))
  }, [selectedAssignment, selectedSubmission])

  useEffect(() => {
    setShowAssessmentSubmissionEditor(!selectedSubmission)
  }, [selectedAssignmentId, selectedSubmission])

  useEffect(() => {
    setIsAssignmentReviewOpen(false)
  }, [selectedAssignmentId, selectedSubmission?.id])

  useEffect(() => {
    if (!submissionAssessmentEntries.length) {
      setAssessmentBySubmission({})
      return
    }

    let isMounted = true

    Promise.all(
      submissionAssessmentEntries.map(async ([assignmentId, submission]) => {
        try {
          const snapshot = await getDoc(doc(db, 'assignmentSubmissionAssessments', submission.id))
          return [assignmentId, submission.id, snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null]
        } catch (error) {
          console.error('Error loading assignment review summary:', error)
          return [assignmentId, submission.id, null]
        }
      })
    ).then((results) => {
      if (!isMounted) return

      setAssessmentBySubmission((current) => {
        const next = { ...current }
        results.forEach(([, submissionId, assessment]) => {
          next[submissionId] = assessment
        })
        return next
      })

      setAssignmentSubmissions((current) => {
        let hasChanges = false
        const next = { ...current }

        results.forEach(([assignmentId, , assessment]) => {
          if (!hasSavedCompletedAssessment(assessment)) return
          const existingSubmission = next[assignmentId]
          if (!existingSubmission) return

          if (
            existingSubmission.reviewStatus === 'reviewed' &&
            existingSubmission.status === 'reviewed' &&
            existingSubmission.aiAssessmentStatus === 'completed'
          ) {
            return
          }

          hasChanges = true
          next[assignmentId] = {
            ...existingSubmission,
            reviewStatus: 'reviewed',
            status: 'reviewed',
            aiAssessmentStatus: 'completed'
          }
        })

        return hasChanges ? next : current
      })
    })

    return () => {
      isMounted = false
    }
  }, [submissionAssessmentEntries, submissionAssessmentKey])

  useEffect(() => {
    if (!selectedSubmission?.id) {
      setAssessmentLoading(false)
      setAssessmentError('')
      return
    }

    let isMounted = true
    setAssessmentLoading(true)
    setAssessmentError('')

    getDoc(doc(db, 'assignmentSubmissionAssessments', selectedSubmission.id))
      .then((snapshot) => {
        if (!isMounted) return
        const nextAssessment = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
        setAssessmentBySubmission((current) => ({
          ...current,
          [selectedSubmission.id]: nextAssessment
        }))
        if (hasSavedCompletedAssessment(nextAssessment) && selectedAssignment?.id) {
          setAssignmentSubmissions((current) => {
            const existingSubmission = current[selectedAssignment.id]
            if (!existingSubmission) return current
            return {
              ...current,
              [selectedAssignment.id]: {
                ...existingSubmission,
                reviewStatus: 'reviewed',
                status: 'reviewed',
                aiAssessmentStatus: 'completed'
              }
            }
          })
        }
      })
      .catch((error) => {
        console.error('Error loading student assignment assessment:', error)
        if (!isMounted) return
        setAssessmentError('We could not load your AI learning overview right now.')
      })
      .finally(() => {
        if (isMounted) setAssessmentLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [selectedAssignment?.id, selectedSubmission?.id])

  useEffect(() => {
    if (!userId || !selectedAssignment?.id || !isAssessmentOpen) return

    setDoc(doc(db, 'studentAssignmentReads', buildAssignmentReadDocId(userId, selectedAssignment.id)), {
      studentId: userId,
      assignmentId: selectedAssignment.id,
      courseName: selectedAssignment.courseName || courseName || '',
      readAt: serverTimestamp(),
      readAtMs: Date.now()
    }, { merge: true }).catch((error) => {
      console.error('Error marking student assignment as read:', error)
    })
  }, [courseName, isAssessmentOpen, selectedAssignment?.courseName, selectedAssignment?.id, userId])

  const openAssessmentAssignment = (assignmentId) => {
    setSelectedAssignmentId(assignmentId)
    setSubmissionNotice('')
    setAssessmentError('')
    setIsAssignmentReviewOpen(false)
    setIsAssessmentOpen(true)
  }

  const focusStudentEditor = (editor) => {
    editor?.focus()
  }

  const updatePrimaryResponseEditor = () => {
    const html = finalizeStudentResponseHtml(
      primaryResponseEditorRef.current?.innerHTML || '',
      assignmentExpectsCodeResponse(selectedAssignment?.assignmentType)
    )
    setSubmissionForm((current) => ({
      ...current,
      responseTextHtml: html,
      responseText: getPlainTextFromHtml(html)
    }))
  }

  const updateNotesEditor = () => {
    const html = finalizeStudentResponseHtml(notesEditorRef.current?.innerHTML || '')
    setSubmissionForm((current) => ({
      ...current,
      notesHtml: html,
      notes: getPlainTextFromHtml(html)
    }))
  }

  const updatePromptResponseEditor = (index) => {
    const html = finalizeStudentResponseHtml(
      promptResponseEditorRefs.current[index]?.innerHTML || '',
      assignmentExpectsCodeResponse(selectedAssignment?.assignmentType)
    )

    setSubmissionForm((current) => {
      const responseItemsHtml = [...(current.responseItemsHtml || [])]
      const responseItems = [...(current.responseItems || [])]
      responseItemsHtml[index] = html
      responseItems[index] = getPlainTextFromHtml(html)
      return {
        ...current,
        responseItemsHtml,
        responseItems
      }
    })
  }

  const applyStudentEditorCommand = (editor, syncEditor, command) => {
    focusStudentEditor(editor)
    document.execCommand(command, false)
    syncEditor()
  }

  const insertStudentEditorList = (editor, syncEditor) => {
    focusStudentEditor(editor)
    document.execCommand('insertUnorderedList', false)
    syncEditor()
  }

  const insertStudentEditorCode = (editor, syncEditor, preferBlock = false) => {
    focusStudentEditor(editor)
    const selection = window.getSelection()
    const selectedText = selection ? selection.toString() : ''
    const shouldUseBlock = preferBlock || selectedText.includes('\n')
    const html = shouldUseBlock
      ? `<pre><code>${escapeHtml(selectedText || 'code')}</code></pre>`
      : `<code>${escapeHtml(selectedText || 'code')}</code>`
    document.execCommand('insertHTML', false, html)
    syncEditor()
  }

  const handleStudentEditorPaste = (event, syncEditor, preferCode = false) => {
    const pastedText = event.clipboardData?.getData('text/plain') || ''
    if (!pastedText) return

    event.preventDefault()

    const looksLikeCode = preferCode || pastedText.includes('\n')
    const html = looksLikeCode
      ? `<pre><code>${escapeHtml(pastedText)}</code></pre>`
      : pastedText
        .split(/\n{2,}/)
        .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
        .join('')

    document.execCommand('insertHTML', false, html)
    syncEditor()
  }

  const updateSubmissionField = (field, value) => {
    setSubmissionForm((current) => ({ ...current, [field]: value }))
  }

  const updateSubmissionResponseItem = (index, value) => {
    setSubmissionForm((current) => ({
      ...current,
      responseItems: (current.responseItems || []).map((item, itemIndex) => (
        itemIndex === index ? value : item
      )),
      responseItemsHtml: (current.responseItemsHtml || []).map((item, itemIndex) => (
        itemIndex === index ? createEditorHtmlFromPlainText(value, assignmentExpectsCodeResponse(selectedAssignment?.assignmentType)) : item
      ))
    }))
  }

  const submitAssignment = async (event) => {
    event.preventDefault()

    if (!selectedAssignment || !userId) return

    const normalizedResponseItemsHtml = Array.isArray(submissionForm.responseItemsHtml)
      ? submissionForm.responseItemsHtml.map((value) => finalizeStudentResponseHtml(value, assignmentExpectsCodeResponse(selectedAssignment.assignmentType)))
      : []
    const normalizedResponseItems = normalizedResponseItemsHtml.map((value) => getPlainTextFromHtml(value))
    const normalizedResponseTextHtml = finalizeStudentResponseHtml(
      submissionForm.responseTextHtml || '',
      assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
    )
    const normalizedResponseText = getPlainTextFromHtml(normalizedResponseTextHtml)
    const normalizedNotesHtml = finalizeStudentResponseHtml(submissionForm.notesHtml || '')
    const normalizedNotes = getPlainTextFromHtml(normalizedNotesHtml)
    const hasStructuredResponse = normalizedResponseItems.some(Boolean)
    const hasContent = (
      hasStructuredResponse ||
      normalizedResponseText ||
      normalizeText(submissionForm.githubLink) ||
      normalizeText(submissionForm.projectLink) ||
      normalizeText(submissionForm.demoVideoLink) ||
      normalizedNotes
    )
    if (!hasContent) {
      setSubmissionNotice('Add your answer, links, or notes before submitting this assignment.')
      return
    }

    setSavingSubmission(true)
    setSubmissionNotice('')

    try {
      const existingSubmission = assignmentSubmissions[selectedAssignment.id]
      const submissionState = existingSubmission ? 'resubmitted' : 'submitted'
      const submissionDocId = existingSubmission?.id || buildSubmissionDocId(selectedAssignment.id, userId)
      const nowMs = Date.now()
      const responseText = assignmentUsesPromptItems(selectedAssignment.assignmentType)
        ? (selectedAssignment.promptItems || [])
          .map((prompt, index) => {
            const answer = normalizedResponseItems[index] || ''
            return [`${index + 1}. ${prompt}`, answer].filter(Boolean).join('\n')
          })
          .filter(Boolean)
          .join('\n\n')
        : normalizedResponseText
      const responseTextHtml = assignmentUsesPromptItems(selectedAssignment.assignmentType)
        ? normalizedResponseItemsHtml.join('')
        : normalizedResponseTextHtml
      const payload = {
        assignmentId: selectedAssignment.id,
        assignmentTitle: selectedAssignment.title || 'Assignment',
        assignmentType: selectedAssignment.assignmentType || 'task',
        courseId: activeCourseData?.id || selectedAssignment.courseId || '',
        courseName: selectedAssignment.courseName || courseName,
        studentId: userId,
        userId,
        submittedById: userId,
        studentName: studentProfile?.bio || studentProfile?.userName || studentProfile?.name || 'Student',
        studentEmail: studentProfile?.userEmail || studentProfile?.email || '',
        responseItems: normalizedResponseItems,
        responseItemsHtml: normalizedResponseItemsHtml,
        responseText,
        responseTextHtml,
        answerText: responseText,
        githubLink: normalizeText(submissionForm.githubLink),
        githubUrl: normalizeText(submissionForm.githubLink),
        repositoryUrl: normalizeText(submissionForm.githubLink),
        projectLink: normalizeText(submissionForm.projectLink),
        projectUrl: normalizeText(submissionForm.projectLink),
        liveLink: normalizeText(submissionForm.projectLink),
        demoVideoLink: normalizeText(submissionForm.demoVideoLink),
        videoLink: normalizeText(submissionForm.demoVideoLink),
        videoUrl: normalizeText(submissionForm.demoVideoLink),
        notes: normalizedNotes,
        notesHtml: normalizedNotesHtml,
        status: submissionState,
        reviewStatus: submissionState,
        updatedAt: serverTimestamp(),
        updatedAtMs: nowMs
      }

      if (!existingSubmission) {
        payload.createdAt = serverTimestamp()
        payload.submittedAt = serverTimestamp()
        payload.submittedAtMs = nowMs
      }

      await setDoc(doc(db, 'assignmentSubmissions', submissionDocId), payload, { merge: true })

      setAssignmentSubmissions((current) => ({
        ...current,
        [selectedAssignment.id]: {
          id: submissionDocId,
          ...current[selectedAssignment.id],
          ...payload,
          createdAt: current[selectedAssignment.id]?.createdAt || nowMs,
          submittedAt: current[selectedAssignment.id]?.submittedAt || nowMs,
          updatedAt: nowMs
        }
      }))
      setAssessmentBySubmission((current) => ({
        ...current,
        [submissionDocId]: null
      }))

      setSubmissionNotice('')
      setAssessmentError('')
      setIsAssignmentReviewOpen(false)
      setShowAssessmentSubmissionEditor(false)
      setIsAssessmentOpen(false)
    } catch (error) {
      console.error('Error submitting assignment:', error)
      setSubmissionNotice('We could not submit your work right now. Please try again.')
    } finally {
      setSavingSubmission(false)
    }
  }

  if (loading) {
    return (
      <div className='courseDetailsPage'>
        <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`courseDetailsPage ${embedded ? 'courseDetailsPageEmbedded' : ''}`}>
      {!embedded && (
        <div className='courseDetailsHero'>
          <button className='courseBackBtn' onClick={() => navigate('/academic')}>
            Back to My Courses
          </button>
          <h3>{displayCourseName || 'Course Details'}</h3>
          <p>{displayCourseDesc || ''}</p>
        </div>
      )}

      <div className='courseTabs'>
        <button
          className={activeTab === 'overview' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'scheme' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('scheme')}
        >
          Scheme of Work
        </button>
        <button
          className={activeTab === 'assessments' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('assessments')}
        >
          Assessments
        </button>
        <button
          className={activeTab === 'skills' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('skills')}
        >
          Skills & Competency Ledger
        </button>
        <button
          className={activeTab === 'performance' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>
        <button
          className={activeTab === 'notes' ? 'courseTab active' : 'courseTab'}
          onClick={() => setActiveTab('notes')}
        >
          Learning Profile
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className='courseDetailsGrid'>
          {courseSummary && (
            <div className='coursePanel coursePanelFull courseOverviewSummaryCard'>
              <div className='courseOverviewSummaryHeader'>
                <div>
                  <span className='courseOverviewSummaryEyebrow'>Course summary</span>
                  <h5>{displayCourseName || 'Course Details'}</h5>
                </div>
                <p className='courseDetailsText'>A quick snapshot of your current course setup before you dive into the curriculum and deliverables.</p>
              </div>

              <div className='courseOverviewSummaryGrid'>
                <div className='courseOverviewSummaryItem'>
                  <span>Track</span>
                  <strong>{courseSummary.track}</strong>
                </div>
                <div className='courseOverviewSummaryItem'>
                  <span>Status</span>
                  <strong>{courseSummary.status}</strong>
                </div>
                <div className='courseOverviewSummaryItem'>
                  <span>{courseSummary.durationLabel}</span>
                  <strong>{courseSummary.durationValue}</strong>
                </div>
                <div className='courseOverviewSummaryItem'>
                  <span>Admission</span>
                  <strong>{courseSummary.admission}</strong>
                </div>
              </div>
            </div>
          )}

          <div className='coursePanel'>
            <h5>Curriculum Overview</h5>
            {displayOverview.length > 0 ? (
              <ul className='courseDetailsList'>
                {displayOverview.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className='courseDetailsText'>{loadMessage || 'Admin has not added the curriculum overview yet.'}</p>
            )}
          </div>
          <div className='coursePanel'>
            <h5>Course Deliverables</h5>
            {displayDeliverables.length > 0 ? (
              <ul className='courseDetailsList'>
                {displayDeliverables.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : (
              <p className='courseDetailsText'>Course deliverables will appear here once admin publishes them.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'scheme' && (
        <div className='coursePanel coursePanelFull'>
          <div className='schemeHeader'>
            <div>
              <h5>Scheme of Work</h5>
              <p className='courseDetailsText'>Your instructor updates progress day by day. This view shows your live completion percentage and completed weeks.</p>
            </div>
            <div className='schemeHeaderMeta'>
              <div className='progressChip'>{progressPercent}% complete</div>
              <div className='progressChip progressChipMuted'>
                {completedUnitCount}/{progressUnits.length || 0} class days
              </div>
            </div>
          </div>
          <ProgressBar now={progressPercent} label={`${progressPercent}%`} className='schemeProgress' />

          {schemeItems.length > 0 ? (
            <div className='schemeWeekList'>
              {schemeItems.map((item, weekIndex) => {
                const weekId = getWeekCompletionId(item, weekIndex)
                const weekUnits = getWeekProgressUnits(item, weekIndex)
                const completedUnits = weekUnits.filter((unit) => completedDaySet.has(unit.key)).length
                const isWeekComplete = completedUnits > 0 && completedUnits === weekUnits.length
                const isWeekInProgress = completedUnits > 0 && completedUnits < weekUnits.length

                return (
                  <article
                    key={weekId}
                    className={`schemeWeekCard ${isWeekComplete ? 'schemeWeekDone' : ''} ${isWeekInProgress ? 'schemeWeekInProgress' : ''}`}
                  >
                    <div className='schemeWeekTop'>
                      <div>
                        <span className='schemeWeekBadge'>Week {item.week}</span>
                        <h5>{item.topic}</h5>
                        <p className='courseDetailsText'>{item.objectives}</p>
                      </div>
                      <div className={`schemeStatusBadge ${isWeekComplete ? 'schemeStatusBadgeComplete' : isWeekInProgress ? 'schemeStatusBadgeProgress' : 'schemeStatusBadgePending'}`}>
                        {isWeekComplete ? 'Completed' : isWeekInProgress ? `${completedUnits}/${weekUnits.length} complete` : 'Not started'}
                      </div>
                    </div>

                    {item.dailyPlan?.length ? (
                      <div className='schemeTableWrap'>
                        <table className='schemeTable schemeDayTable'>
                          <thead>
                            <tr>
                              <th>Day</th>
                              <th>Class Day</th>
                              <th>Daily Activity</th>
                              <th>Teaching Aid</th>
                              <th>Reference</th>
                              <th>Progress</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getWeekProgressUnits(item, weekIndex).map((day) => {
                              const isCompleted = completedDaySet.has(day.key)
                              return (
                                <tr key={day.key} className={isCompleted ? 'schemeRowComplete' : ''}>
                                  <td>{day.day}</td>
                                  <td>{day.label}</td>
                                  <td>{day.focus}</td>
                                  <td>{day.teachingAid || item.teachingAid}</td>
                                  <td>{day.reference || item.reference}</td>
                                  <td>
                                    <span className={`schemeDayStatus ${isCompleted ? 'schemeDayStatusDone' : 'schemeDayStatusPending'}`}>
                                      {isCompleted ? 'Done' : 'Pending'}
                                    </span>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className='courseDetailsText'>Daily breakdown will be added for this week.</p>
                    )}

                    <div className='schemeWeekMeta'>
                      <div>
                        <span>Class Activities</span>
                        <p>{item.activities}</p>
                      </div>
                      <div>
                        <span>Teaching Aid</span>
                        <p>{item.teachingAid}</p>
                      </div>
                      <div>
                        <span>Reference</span>
                        <p>{item.reference}</p>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <p className='courseDetailsText'>No scheme of work available yet.</p>
          )}
        </div>
      )}

      {activeTab === 'assessments' && (
        <div className='coursePanel coursePanelFull'>
          <div className='assessmentHeader'>
            <div>
              <h5>Assessment Tracker</h5>
              <p className='courseDetailsText'>See every assignment your instructor has issued for this course and submit your work directly here.</p>
            </div>
            <div className='progressChip'>{assignmentStats.total} live assignments</div>
          </div>

          <div className='assessmentCards'>
            {assignmentSummaryCards.map((card) => (
              <div className={`assessmentCard ${card.className}`} key={card.key}>
                <div className='assessmentCardTop'>
                  <p>{card.label}</p>
                  <span className='assessmentCardBadge'>{assignmentStats.total} total</span>
                </div>
                <div className='assessmentCardValueRow'>
                  <h3>{card.value}</h3>
                  <strong>{assignmentStats.total > 0 ? Math.round((card.value / assignmentStats.total) * 100) : 0}%</strong>
                </div>
                <span>{card.note}</span>
              </div>
            ))}
          </div>

          {assignments.length > 0 ? (
            !isAssessmentOpen ? (
              <div className='studentAssignmentLauncher'>
                {assignments.map((assignment) => {
                  const submission = assignmentSubmissions[assignment.id]
                  const statusKey = getAssignmentStatusKey(submission, submission?.id ? assessmentBySubmission[submission.id] : null)

                  return (
                    <button
                      key={assignment.id}
                      type='button'
                      className='studentAssignmentCard studentAssignmentLauncherCard'
                      onClick={() => openAssessmentAssignment(assignment.id)}
                    >
                      <div className='studentAssignmentCardTop'>
                        <div>
                          <span className='studentAssignmentEyebrow'>{getAssignmentTypeLabel(assignment)}</span>
                          <h4>{assignment.title || 'Untitled assignment'}</h4>
                        </div>
                        <span className={`studentAssignmentStatus studentAssignmentStatus-${statusKey}`}>
                          {getAssignmentStatusLabel(submission, submission?.id ? assessmentBySubmission[submission.id] : null)}
                        </span>
                      </div>

                      <div className='studentAssignmentMeta'>
                        <span>Due {assignment.dueDate ? formatCalendarDate(assignment.dueDate) : 'To be shared'}</span>
                        <span>{submission ? `Updated ${formatCalendarDate(submission.updatedAt || submission.submittedAt || submission.createdAt)}` : 'Awaiting submission'}</span>
                      </div>

                      <div className='studentAssignmentOpenHint'>Open assignment</div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <section className='studentAssignmentFullscreen'>
                <div className='studentAssignmentFullscreenBar'>
                  <button
                    type='button'
                    className='studentAssignmentBackButton'
                    onClick={() => {
                      setIsAssessmentOpen(false)
                      setSubmissionNotice('')
                    }}
                  >
                    Back to assessments
                  </button>
                </div>

                <div className='studentAssignmentEditor studentAssignmentEditorFull'>
                  {selectedAssignment ? (
                    isAssignmentReviewOpen && selectedSubmissionStatusKey === 'reviewed' ? (
                      <section className='studentAssignmentReviewFullscreen'>
                        <div className='studentAssignmentReviewFullscreenTop'>
                          <button
                            type='button'
                            className='studentAssignmentBackButton'
                            onClick={() => setIsAssignmentReviewOpen(false)}
                          >
                            Back to assignment
                          </button>
                          <span className={`studentAssignmentStatus studentAssignmentStatus-${getAssignmentStatusKey(selectedSubmission, selectedAssessment)}`}>
                            {getAssignmentStatusLabel(selectedSubmission, selectedAssessment)}
                          </span>
                        </div>

                        {(assessmentLoading || selectedAssessment) && (
                          <div className='assignmentsAssessmentPanel studentAssignmentReviewFullscreenPanel'>
                            <div className='assignmentsOverviewHeader'>
                              <div>
                                <span className='assignmentsDetailEyebrow'>AI learning review</span>
                                <h4>{selectedAssessment?.assessment?.summary?.overallSignal || 'Saved assessment'}</h4>
                              </div>
                              <div className='assignmentsOverviewCount'>
                                {selectedAssessment?.assessment ? 'Review ready' : 'Awaiting run'}
                              </div>
                            </div>

                            {assessmentLoading && !selectedAssessment ? (
                              <p className='assignmentsMuted'>Loading the saved assessment...</p>
                            ) : selectedAssessment?.status === 'failed' ? (
                              <p className='assignmentsMuted'>{selectedAssessment.error || 'The last AI assessment run failed.'}</p>
                            ) : selectedAssessment?.assessment ? (
                              <div className='assignmentsAssessmentGrid'>
                                <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                  <span>Instructor summary</span>
                                  <h5>{selectedAssessment.assessment.summary?.headline || 'Detailed learner assessment'}</h5>
                                  <p>{selectedAssessment.assessment.summary?.learnerSnapshot || selectedAssessment.assessment.summary?.futureOfLearningLens || 'No summary was generated yet.'}</p>
                                  <div className='assignmentsAssessmentMeta'>
                                    <strong>{selectedAssessment.assessment.summary?.confidence || 'Confidence pending'}</strong>
                                    <span>{selectedAssessment.assessment.summary?.confidenceReason || selectedAssessment.assessment.summary?.evidenceCoverage || 'Evidence coverage will appear here.'}</span>
                                  </div>
                                </article>

                                <article className='assignmentsAssessmentCard'>
                                  <span>Future readiness</span>
                                  <h5>{selectedAssessment.assessment.summary?.futureReadiness || 'Signal pending'}</h5>
                                  <p>{selectedAssessment.assessment.summary?.overallAssessment || 'The system will explain the learner’s trajectory here.'}</p>
                                </article>

                                {normalizeText(selectedAssessment.assessment.summary?.curriculumAlignment) && (
                                  <article className='assignmentsAssessmentCard'>
                                    <span>Curriculum alignment</span>
                                    <h5>{selectedAssessment.curriculumSnapshot?.selectedWeek?.label || getAssignmentWeekLabel(selectedAssignment) || 'Selected teaching-plan week'}</h5>
                                    <p>{selectedAssessment.assessment.summary.curriculumAlignment}</p>
                                  </article>
                                )}

                                {rubricChartItems.length > 0 && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Performance snapshot</span>
                                    <h5>Rubric score profile</h5>
                                    <div className='assignmentsAssessmentChart' aria-label='Rubric performance chart'>
                                      {rubricChartItems.map((item) => (
                                        <div key={item.id} className='assignmentsAssessmentChartColumn'>
                                          <strong>{renderAssessmentScore(item.score, item.outOf)}</strong>
                                          <div className='assignmentsAssessmentChartBar'>
                                            <div style={{ height: `${Math.max(item.percent, 10)}%` }} />
                                          </div>
                                          <span title={item.label}>{item.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {normalizeAssessmentList(selectedAssessment.assessment.rubric).length > 0 && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Rubric dimensions</span>
                                    <div className='assignmentsAssessmentList'>
                                      {selectedAssessment.assessment.rubric.map((item, index) => (
                                        <div key={`${item.id || item.label}-${index}`}>
                                          <strong>{item.label} • {renderAssessmentScore(item.score, item.outOf || 5)}</strong>
                                          <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                            <div style={{ width: `${getAssessmentScorePercent(item.score, item.outOf || 5)}%` }} />
                                          </div>
                                          <p>{item.rationale}</p>
                                          {item.nextMove && <small>Next move: {item.nextMove}</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {normalizeAssessmentList(selectedAssessment.assessment.strengths).length > 0 && (
                                  <article className='assignmentsAssessmentCard'>
                                    <span>Strengths</span>
                                    <div className='assignmentsAssessmentList'>
                                      {selectedAssessment.assessment.strengths.map((item, index) => (
                                        <div key={`${item.title}-${index}`}>
                                          <strong>{item.title}</strong>
                                          <p>{item.whyItMatters}</p>
                                          {item.evidence && <small>Evidence: {item.evidence}</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {normalizeAssessmentList(selectedAssessment.assessment.growthAreas).length > 0 && (
                                  <article className='assignmentsAssessmentCard'>
                                    <span>Growth areas</span>
                                    <div className='assignmentsAssessmentList'>
                                      {selectedAssessment.assessment.growthAreas.map((item, index) => (
                                        <div key={`${item.title}-${index}`}>
                                          <strong>{item.title}</strong>
                                          <p>{item.whyItMatters}</p>
                                          {item.intervention && <small>Intervention: {item.intervention}</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {normalizeAssessmentList(selectedAssessment.assessment.misconceptions).length > 0 && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Misconceptions and teaching moves</span>
                                    <div className='assignmentsAssessmentList'>
                                      {selectedAssessment.assessment.misconceptions.map((item, index) => (
                                        <div key={`${item.concept}-${index}`}>
                                          <strong>{item.concept}</strong>
                                          <p>{item.correction}</p>
                                          {item.teachingMove && <small>Teaching move: {item.teachingMove}</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {normalizeAssessmentList(selectedAssessment.assessment.promptFindings).length > 0 && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Prompt-by-prompt findings</span>
                                    <div className='assignmentsAssessmentList'>
                                      {selectedAssessment.assessment.promptFindings.map((item, index) => (
                                        <div key={`${item.promptLabel || item.promptNumber}-${index}`}>
                                          <strong>{item.promptLabel || `Prompt ${index + 1}`} • {renderAssessmentScore(item.score, 5)}</strong>
                                          <div className='assignmentsAssessmentMeter' aria-hidden='true'>
                                            <div style={{ width: `${getAssessmentScorePercent(item.score, 5)}%` }} />
                                          </div>
                                          <p>{item.responseSummary || item.masterySignal}</p>
                                          {item.coachingMove && <small>Coaching move: {item.coachingMove}</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </article>
                                )}

                                {(normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.immediateActions).length > 0
                                  || normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.nextAssignmentAdjustments).length > 0
                                  || normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.futureReadyNextSteps).length > 0) && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Teaching plan</span>
                                    <div className='assignmentsAssessmentBulletGrid'>
                                      {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.immediateActions).length > 0 && (
                                        <div>
                                          <strong>Immediate instructor actions</strong>
                                          {selectedAssessment.assessment.teachingPlan.immediateActions.map((item, index) => <p key={`action-${index}`}>{item}</p>)}
                                        </div>
                                      )}
                                      {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.nextAssignmentAdjustments).length > 0 && (
                                        <div>
                                          <strong>Next assignment adjustments</strong>
                                          {selectedAssessment.assessment.teachingPlan.nextAssignmentAdjustments.map((item, index) => <p key={`adjustment-${index}`}>{item}</p>)}
                                        </div>
                                      )}
                                      {normalizeAssessmentList(selectedAssessment.assessment.teachingPlan?.futureReadyNextSteps).length > 0 && (
                                        <div>
                                          <strong>Future-ready next steps</strong>
                                          {selectedAssessment.assessment.teachingPlan.futureReadyNextSteps.map((item, index) => <p key={`future-${index}`}>{item}</p>)}
                                        </div>
                                      )}
                                    </div>
                                  </article>
                                )}

                                {(normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.celebration).length > 0
                                  || normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.improve).length > 0
                                  || normalizeText(selectedAssessment.assessment.studentFeedback?.nextChallenge)) && (
                                  <article className='assignmentsAssessmentCard assignmentsAssessmentCardWide'>
                                    <span>Student-facing feedback</span>
                                    <div className='assignmentsAssessmentBulletGrid'>
                                      {normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.celebration).length > 0 && (
                                        <div>
                                          <strong>Celebrate</strong>
                                          {selectedAssessment.assessment.studentFeedback.celebration.map((item, index) => <p key={`celebrate-${index}`}>{item}</p>)}
                                        </div>
                                      )}
                                      {normalizeAssessmentList(selectedAssessment.assessment.studentFeedback?.improve).length > 0 && (
                                        <div>
                                          <strong>Improve</strong>
                                          {selectedAssessment.assessment.studentFeedback.improve.map((item, index) => <p key={`improve-${index}`}>{item}</p>)}
                                        </div>
                                      )}
                                      {normalizeText(selectedAssessment.assessment.studentFeedback?.nextChallenge) && (
                                        <div>
                                          <strong>Next challenge</strong>
                                          <p>{selectedAssessment.assessment.studentFeedback.nextChallenge}</p>
                                        </div>
                                      )}
                                    </div>
                                  </article>
                                )}
                              </div>
                            ) : (
                              <p className='assignmentsMuted'>Run the AI review to generate a detailed learner analysis for this submission.</p>
                            )}
                          </div>
                        )}
                      </section>
                    ) : (
                    <>
                      <div className='studentAssignmentEditorTop'>
                        <div className='studentAssignmentEditorIntro'>
                          <span className='studentAssignmentEyebrow'>Assignment workspace</span>
                          <h4>{selectedAssignment.title || 'Assignment'}</h4>
                          <div
                            className='studentAssignmentRichText'
                            dangerouslySetInnerHTML={{ __html: getAssignmentDisplayHtml(selectedAssignment) }}
                          />
                        </div>
                        <div className='studentAssignmentTopAside'>
                          <span className={`studentAssignmentStatus studentAssignmentStatus-${getAssignmentStatusKey(selectedSubmission, selectedAssessment)}`}>
                            {getAssignmentStatusLabel(selectedSubmission, selectedAssessment)}
                          </span>

                          {selectedSubmissionStatusKey === 'reviewed' && (
                            <div className='studentAssignmentReviewHero'>
                              <span className='studentAssignmentReviewHeroEyebrow'>Review ready</span>
                              <strong>View your assignment review</strong>
                              <p>Open your detailed feedback, strengths, rubric snapshot, and next steps.</p>
                              <button
                                type='button'
                                className='studentAssignmentSecondaryButton studentAssignmentReviewButton'
                                onClick={() => setIsAssignmentReviewOpen((current) => !current)}
                              >
                                {isAssignmentReviewOpen ? 'Hide assignment review' : 'View assignment review'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className='studentAssignmentInfoGrid'>
                        <div>
                          <span>Due date</span>
                          <strong>{selectedAssignment.dueDate ? formatCalendarDate(selectedAssignment.dueDate) : 'No due date'}</strong>
                        </div>
                        <div>
                          <span>Type</span>
                          <strong>{getAssignmentTypeLabel(selectedAssignment)}</strong>
                        </div>
                        <div>
                          <span>Last update</span>
                          <strong>{selectedSubmission ? formatCalendarDate(selectedSubmission.updatedAt || selectedSubmission.submittedAt || selectedSubmission.createdAt) : 'Not submitted yet'}</strong>
                        </div>
                      </div>

                      {selectedAssignment.resourceLink && (
                        <a
                          href={selectedAssignment.resourceLink}
                          target='_blank'
                          rel='noreferrer'
                          className='studentAssignmentResource'
                        >
                          Open assignment resource
                        </a>
                      )}

                      {getAssignmentAttachment(selectedAssignment) && (
                        <div className='studentAssignmentSubmissionLinksBlock'>
                          <span className='studentAssignmentEyebrow'>Assignment file</span>
                          <div className='studentAssignmentLinks'>
                            <a
                              href={getAssignmentAttachment(selectedAssignment).url}
                              target='_blank'
                              rel='noreferrer'
                              download={getAssignmentAttachment(selectedAssignment).name}
                              className='studentAssignmentLinkCard'
                            >
                              <span>Download file</span>
                              <strong>
                                {getAssignmentAttachment(selectedAssignment).name}
                                {formatFileSize(getAssignmentAttachment(selectedAssignment).size)
                                  ? ` • ${formatFileSize(getAssignmentAttachment(selectedAssignment).size)}`
                                  : ''}
                              </strong>
                            </a>
                          </div>
                        </div>
                      )}

                      {selectedSubmission && (
                        <div className='studentAssignmentSubmittedBlock'>
                          <div className='studentAssignmentSectionTop'>
                            <div>
                              <span className='studentAssignmentEyebrow'>Your submission</span>
                              <h5>Submitted work</h5>
                            </div>
                            <span className={`studentAssignmentStatus studentAssignmentStatus-${getAssignmentStatusKey(selectedSubmission, selectedAssessment)}`}>
                              {getAssignmentStatusLabel(selectedSubmission, selectedAssessment)}
                            </span>
                          </div>

                          <div className='studentAssignmentSubmissionList'>
                            {Array.isArray(selectedAssignment.promptItems) && selectedAssignment.promptItems.length > 0 && (
                              <div className='studentAssignmentSubmissionDetailGroup'>
                                {selectedAssignment.promptItems.map((prompt, index) => {
                                  const answerText = selectedSubmission.responseItems?.[index] || ''
                                  const answerHtml = selectedSubmission.responseItemsHtml?.[index]
                                    ? sanitizeRichTextHtml(selectedSubmission.responseItemsHtml[index])
                                    : ''
                                  const hasAnswer = normalizeText(answerText) || normalizeText(answerHtml)

                                  return (
                                    <div className='studentAssignmentSubmissionQuestionBlock' key={`${selectedAssignment.id}-submitted-${index}`}>
                                      <div
                                        className='studentAssignmentRichText studentAssignmentPromptViewItem'
                                        dangerouslySetInnerHTML={{
                                          __html: `<span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}`
                                        }}
                                      />
                                      <div className='studentAssignmentSubmissionAnswer'>
                                        <span>Student response</span>
                                        {hasAnswer ? (
                                          answerHtml ? (
                                            <div
                                              className='studentAssignmentRenderedRichText'
                                              dangerouslySetInnerHTML={{ __html: answerHtml }}
                                            />
                                          ) : assignmentExpectsCodeResponse(selectedAssignment.assignmentType) ? <pre>{answerText}</pre> : <p>{answerText}</p>
                                        ) : (
                                          <p>No response was submitted for this question.</p>
                                        )}
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}

                            {(!Array.isArray(selectedAssignment.promptItems) || selectedAssignment.promptItems.length === 0) &&
                              (normalizeText(selectedSubmission.responseText || selectedSubmission.answerText) || normalizeText(selectedSubmission.responseTextHtml)) && (
                              <div className='studentAssignmentSubmissionAnswer'>
                                <span>{assignmentExpectsCodeResponse(selectedAssignment.assignmentType) ? 'Submitted code' : 'Submitted response'}</span>
                                {selectedSubmission.responseTextHtml ? (
                                  <div
                                    className='studentAssignmentRenderedRichText'
                                    dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(selectedSubmission.responseTextHtml) }}
                                  />
                                ) : assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                                  ? <pre>{normalizeText(selectedSubmission.responseText || selectedSubmission.answerText)}</pre>
                                  : <p>{normalizeText(selectedSubmission.responseText || selectedSubmission.answerText)}</p>}
                              </div>
                            )}

                            {(normalizeText(selectedSubmission.notes || selectedSubmission.comment) || normalizeText(selectedSubmission.notesHtml)) && (
                              <div className='studentAssignmentSubmissionAnswer'>
                                <span>Note to instructor</span>
                                {selectedSubmission.notesHtml ? (
                                  <div
                                    className='studentAssignmentRenderedRichText'
                                    dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(selectedSubmission.notesHtml) }}
                                  />
                                ) : <p>{normalizeText(selectedSubmission.notes || selectedSubmission.comment)}</p>}
                              </div>
                            )}
                          </div>

                          {getSubmissionLinks(selectedSubmission).length > 0 && (
                            <div className='studentAssignmentSubmissionLinksBlock'>
                              <span className='studentAssignmentEyebrow'>Shared links</span>
                              <div className='studentAssignmentLinks'>
                                {getSubmissionLinks(selectedSubmission).map((link) => (
                                  <a
                                    key={`${selectedAssignment.id}-${link.label}`}
                                    className='studentAssignmentLinkCard'
                                    href={link.value}
                                    target='_blank'
                                    rel='noreferrer'
                                  >
                                    <span>{link.label}</span>
                                    <strong>{link.value}</strong>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedSubmission && !showAssessmentSubmissionEditor && (
                        <div className='studentAssignmentInlineActions'>
                          <button
                            type='button'
                            className='studentAssignmentSecondaryButton'
                            onClick={() => {
                              setShowAssessmentSubmissionEditor(true)
                              setSubmissionNotice('')
                            }}
                          >
                            Update submission
                          </button>
                        </div>
                      )}

                      {(!selectedSubmission || showAssessmentSubmissionEditor) && (selectedAssignment.promptItems?.length > 0) && (
                        <div className='studentAssignmentQuestionSet'>
                          {selectedAssignment.promptItems.map((prompt, index) => (
                            <label className='studentAssignmentField' key={`${selectedAssignment.id}-prompt-${index}`}>
                              <span>Question {index + 1}</span>
                              <div
                                className='studentAssignmentPromptText'
                                dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(prompt) }}
                              />
                              <div className='studentAssignmentEditorShell'>
                                <div className='studentAssignmentEditorTools'>
                                  <button type='button' onClick={() => applyStudentEditorCommand(promptResponseEditorRefs.current[index], () => updatePromptResponseEditor(index), 'bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                                  <button type='button' onClick={() => applyStudentEditorCommand(promptResponseEditorRefs.current[index], () => updatePromptResponseEditor(index), 'underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                                  <button type='button' onClick={() => insertStudentEditorList(promptResponseEditorRefs.current[index], () => updatePromptResponseEditor(index))} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                                  <button type='button' onClick={() => insertStudentEditorCode(promptResponseEditorRefs.current[index], () => updatePromptResponseEditor(index), assignmentExpectsCodeResponse(selectedAssignment.assignmentType))} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                                </div>
                                <div
                                  className='studentAssignmentEditorCanvas'
                                  ref={(node) => {
                                    if (node) {
                                      promptResponseEditorRefs.current[index] = node
                                      const html = submissionForm.responseItemsHtml?.[index] || ''
                                      if (node.innerHTML !== html) node.innerHTML = html
                                    } else {
                                      delete promptResponseEditorRefs.current[index]
                                    }
                                  }}
                                  contentEditable
                                  suppressContentEditableWarning
                                  role='textbox'
                                  aria-multiline='true'
                                  data-placeholder={
                                    assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                                      ? 'Paste your code answer for this question here.'
                                      : 'Write your answer for this question here.'
                                  }
                                  onInput={() => updatePromptResponseEditor(index)}
                                  onPaste={(event) => handleStudentEditorPaste(
                                    event,
                                    () => updatePromptResponseEditor(index),
                                    assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                                  )}
                                />
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {(!selectedSubmission || showAssessmentSubmissionEditor) && (
                        <form className='studentAssignmentForm' onSubmit={submitAssignment}>
                        {!assignmentUsesPromptItems(selectedAssignment.assignmentType) && (
                          <label className='studentAssignmentField'>
                            <span>{getSubmissionPrimaryLabel(selectedAssignment.assignmentType)}</span>
                            <div className='studentAssignmentEditorShell'>
                              <div className='studentAssignmentEditorTools'>
                                <button type='button' onClick={() => applyStudentEditorCommand(primaryResponseEditorRef.current, updatePrimaryResponseEditor, 'bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                                <button type='button' onClick={() => applyStudentEditorCommand(primaryResponseEditorRef.current, updatePrimaryResponseEditor, 'underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                                <button type='button' onClick={() => insertStudentEditorList(primaryResponseEditorRef.current, updatePrimaryResponseEditor)} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                                <button type='button' onClick={() => insertStudentEditorCode(primaryResponseEditorRef.current, updatePrimaryResponseEditor, assignmentExpectsCodeResponse(selectedAssignment.assignmentType))} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                              </div>
                              <div
                                className='studentAssignmentEditorCanvas'
                                ref={(node) => {
                                  if (node) {
                                    primaryResponseEditorRef.current = node
                                    const html = submissionForm.responseTextHtml || ''
                                    if (node.innerHTML !== html) node.innerHTML = html
                                  }
                                }}
                                contentEditable
                                suppressContentEditableWarning
                                role='textbox'
                                aria-multiline='true'
                                data-placeholder={getSubmissionPrimaryPlaceholder(selectedAssignment.assignmentType)}
                                onInput={updatePrimaryResponseEditor}
                                onPaste={(event) => handleStudentEditorPaste(
                                  event,
                                  updatePrimaryResponseEditor,
                                  assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                                )}
                              />
                            </div>
                          </label>
                        )}

                        <div className='studentAssignmentFieldGrid'>
                          {assignmentAllowsGithubLink(selectedAssignment.assignmentType) && (
                            <label className='studentAssignmentField'>
                              <span>GitHub repository</span>
                              <input
                                type='url'
                                value={submissionForm.githubLink}
                                onChange={(event) => updateSubmissionField('githubLink', event.target.value)}
                                placeholder='https://github.com/your-project'
                              />
                            </label>
                          )}

                          {assignmentAllowsProjectLink(selectedAssignment.assignmentType) && (
                            <label className='studentAssignmentField'>
                              <span>Project or live link</span>
                              <input
                                type='url'
                                value={submissionForm.projectLink}
                                onChange={(event) => updateSubmissionField('projectLink', event.target.value)}
                                placeholder='https://your-project-link.com'
                              />
                            </label>
                          )}

                          {assignmentAllowsDemoVideo(selectedAssignment.assignmentType) && (
                            <label className='studentAssignmentField'>
                              <span>Demo video link</span>
                              <input
                                type='url'
                                value={submissionForm.demoVideoLink}
                                onChange={(event) => updateSubmissionField('demoVideoLink', event.target.value)}
                                placeholder='https://loom.com/...'
                              />
                            </label>
                          )}
                        </div>

                        <label className='studentAssignmentField'>
                          <span>Note to instructor</span>
                          <div className='studentAssignmentEditorShell'>
                            <div className='studentAssignmentEditorTools'>
                              <button type='button' onClick={() => applyStudentEditorCommand(notesEditorRef.current, updateNotesEditor, 'bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                              <button type='button' onClick={() => applyStudentEditorCommand(notesEditorRef.current, updateNotesEditor, 'underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                              <button type='button' onClick={() => insertStudentEditorList(notesEditorRef.current, updateNotesEditor)} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                              <button type='button' onClick={() => insertStudentEditorCode(notesEditorRef.current, updateNotesEditor)} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                            </div>
                            <div
                              className='studentAssignmentEditorCanvas'
                              ref={(node) => {
                                if (node) {
                                  notesEditorRef.current = node
                                  const html = submissionForm.notesHtml || ''
                                  if (node.innerHTML !== html) node.innerHTML = html
                                }
                              }}
                              contentEditable
                              suppressContentEditableWarning
                              role='textbox'
                              aria-multiline='true'
                              data-placeholder='Add context, blockers, or anything your instructor should know.'
                              onInput={updateNotesEditor}
                              onPaste={(event) => handleStudentEditorPaste(event, updateNotesEditor)}
                            />
                          </div>
                        </label>

                        {submissionNotice && (
                          <div className='studentAssignmentNotice'>{submissionNotice}</div>
                        )}

                        <div className='studentAssignmentFormActions'>
                          {selectedSubmission && (
                            <button
                              type='button'
                              className='studentAssignmentSecondaryButton'
                              onClick={() => {
                                setShowAssessmentSubmissionEditor(false)
                                setSubmissionNotice('')
                                setSubmissionForm(getInitialSubmissionForm(selectedAssignment, selectedSubmission))
                              }}
                            >
                              Cancel
                            </button>
                          )}
                          <button type='submit' className='studentAssignmentSubmit' disabled={savingSubmission}>
                            {savingSubmission
                              ? 'Saving...'
                              : selectedSubmission
                                ? 'Update submission'
                              : 'Submit assignment'}
                          </button>
                        </div>
                        </form>
                      )}
                    </>
                    )
                  ) : (
                    <p className='courseDetailsText'>Select an assignment to view details and submit your work.</p>
                  )}
                </div>
              </section>
            )
          ) : displayAssessments.length > 0 ? (
            <div className='assessmentTableWrap'>
              <table className='assessmentTable'>
                <thead>
                  <tr>
                    <th>Assessment</th>
                    <th>Type</th>
                    <th>Due</th>
                    <th>Status</th>
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {displayAssessments.map((assessment) => (
                    <tr key={assessment.id || assessment.title}>
                      <td>{assessment.title || assessment.name || 'Assessment'}</td>
                      <td>{assessment.type || 'Coursework'}</td>
                      <td>{assessment.due || assessment.dueDate || 'To be shared'}</td>
                      <td>{assessment.status || 'Upcoming'}</td>
                      <td>{assessment.weight || assessment.score || 'To be shared'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className='courseDetailsText'>No live assignments have been issued for this course yet.</p>
          )}
        </div>
      )}

      {activeTab === 'skills' && (
        <div className='coursePanel coursePanelFull'>
          <div className='assessmentHeader'>
            <div>
              <h5>Skills & Competency Ledger</h5>
              <p className='courseDetailsText'>Curriculum anchors, reviewed assignments, and your course progress merged into one live skills view.</p>
            </div>
            <div className='progressChip'>
              {competencyLedgerSummary?.trackedCompetencies || activeCompetencies.length} competencies
            </div>
          </div>
          {activeCompetencies.length > 0 ? (
            <>
              <div className='performanceMiniStats performanceMiniStatsWide'>
                <div className='performanceMiniStat'>
                  <span>Advanced</span>
                  <strong>{competencyStatusBreakdown.advanced}</strong>
                </div>
                <div className='performanceMiniStat'>
                  <span>Proficient</span>
                  <strong>{competencyStatusBreakdown.proficient}</strong>
                </div>
                <div className='performanceMiniStat'>
                  <span>Developing</span>
                  <strong>{competencyStatusBreakdown.developing}</strong>
                </div>
                <div className='performanceMiniStat'>
                  <span>Emerging</span>
                  <strong>{competencyStatusBreakdown.emerging}</strong>
                </div>
              </div>

              {topActiveCompetency ? (
                <div className='performanceLedgerHighlight'>
                  <span className='performanceSignalLabel'>Most evidenced right now</span>
                  <strong>{topActiveCompetency.label}</strong>
                  <p>
                    {topActiveCompetency.evidenceCount} evidence item{topActiveCompetency.evidenceCount === 1 ? '' : 's'} across {topActiveCompetency.sourceCount} source
                    {topActiveCompetency.sourceCount === 1 ? '' : 's'} support this competency.
                  </p>
                </div>
              ) : null}

              <div className='performanceLedgerGrid'>
                {activeCompetencies.map((competency) => (
                  <article className='performanceLedgerCard' key={competency.id}>
                    <div className='performanceLedgerTop'>
                      <div>
                        <h6>{competency.label}</h6>
                        <p>{competency.courses[0] || competencyCourseName}</p>
                      </div>
                      <div className='performanceLedgerScoreWrap'>
                        <span className={`performanceLedgerStatus performanceLedgerStatus--${competency.status}`}>
                          {competency.status}
                        </span>
                        <strong>{competency.score}%</strong>
                      </div>
                    </div>

                    <div className='performanceMiniStats performanceLedgerStats'>
                      <div className='performanceMiniStat'>
                        <span>Evidence</span>
                        <strong>{competency.evidenceCount}</strong>
                      </div>
                      <div className='performanceMiniStat'>
                        <span>Sources</span>
                        <strong>{competency.sourceCount}</strong>
                      </div>
                      <div className='performanceMiniStat'>
                        <span>Confidence</span>
                        <strong>{competency.confidence}</strong>
                      </div>
                    </div>

                    {competency.curriculumAnchors.length > 0 ? (
                      <div className='performanceSignalGroups'>
                        <div>
                          <span className='performanceSignalLabel'>Curriculum anchors</span>
                          <div className='performanceChipRow'>
                            {competency.curriculumAnchors.map((item) => (
                              <span className='performanceChip performanceChipAlt' key={`${competency.id}-anchor-${item}`}>{item}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className='performanceSignalGroups'>
                      <div>
                        <span className='performanceSignalLabel'>Supporting evidence</span>
                        {competency.supportingSignals.length > 0 ? (
                          <ul className='performanceLedgerList'>
                            {competency.supportingSignals.map((item) => (
                              <li key={`${competency.id}-support-${item}`}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className='performanceLedgerEmpty'>Positive evidence will appear here as more reviewed work builds up.</p>
                        )}
                      </div>

                      <div>
                        <span className='performanceSignalLabel'>What still needs work</span>
                        {competency.riskSignals.length > 0 ? (
                          <ul className='performanceLedgerList'>
                            {competency.riskSignals.map((item) => (
                              <li key={`${competency.id}-risk-${item}`}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className='performanceLedgerEmpty'>No recurring risk signal is standing out yet for this competency.</p>
                        )}
                      </div>
                    </div>

                    <div className='performanceSignalGroups'>
                      <div>
                        <span className='performanceSignalLabel'>Next move</span>
                        {competency.nextMoves.length > 0 ? (
                          <div className='performanceChipRow'>
                            {competency.nextMoves.map((item) => (
                              <span className='performanceChip performanceChipWarn' key={`${competency.id}-move-${item}`}>{item}</span>
                            ))}
                          </div>
                        ) : (
                          <p className='performanceLedgerEmpty'>Next-step coaching will appear once stronger evidence is available.</p>
                        )}
                      </div>

                      <div>
                        <span className='performanceSignalLabel'>Evidence sources</span>
                        <div className='performanceChipRow'>
                          {competency.sources.map((item) => (
                            <span className='performanceChip performanceChipMuted' key={`${competency.id}-source-${item}`}>{item}</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {competency.lastUpdatedAtMs ? (
                      <div className='performanceLedgerFooter'>
                        Last updated {formatCalendarDate(competency.lastUpdatedAtMs)}
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </>
          ) : (
            <p className='courseDetailsText'>The competency ledger is waiting for more reviewed assignments, course progress, or course structure before it can build course-specific skills signals.</p>
          )}
        </div>
      )}

      {activeTab === 'performance' && (
        <StudentPerformanceComp
          embedded
          forcedCourseName={displayCourseName}
        />
      )}

      {activeTab === 'notes' && (
        <div className='coursePanel coursePanelFull'>
          <div className='assessmentHeader'>
            <div>
              <h5>Learning Profile</h5>
              <p className='courseDetailsText'>A live learning profile built from your reviewed assignments, change over time, and the study patterns your feedback keeps revealing.</p>
            </div>
            <div className='assessmentHeaderActions'>
              <button type='button' className='courseRefreshButton' onClick={handleRefreshCourseDetails} disabled={loading || profileRefreshLoading}>
                {profileRefreshLoading ? 'Rebuilding...' : loading ? 'Refreshing...' : 'Refresh and rebuild'}
              </button>
              <div className='progressChip'>{generatedAssignmentLearningProfile?.reviewCount || learningProfile.reviewedCount} reviews</div>
            </div>
          </div>

          {profileRefreshMessage && (
            <div className={`studentAssessmentNotice ${profileRefreshMessage.toLowerCase().includes('could not') || profileRefreshMessage.toLowerCase().includes('sign in') ? 'studentAssessmentNoticeError' : ''}`}>
              {profileRefreshMessage}
            </div>
          )}

          {generatedAssignmentLearningProfile ? (
            <div className='studentAssessmentPanel learningProfilePanel learningProfilePanelGenerated'>
              <div className='learningProfileHero'>
                <div className='learningProfileHeroCopy'>
                  <div className='learningProfileHeroBadges'>
                    <span className={`learningProfileStatusPill ${getProfileToneClass(generatedAssignmentLearningProfile.readinessBand, 'learningProfileReadiness')}`}>
                      {generatedAssignmentLearningProfile.readinessBand}
                    </span>
                    <span className={`learningProfileStatusPill ${getProfileToneClass(generatedAssignmentLearningProfile.evidenceStrength, 'learningProfileEvidence')}`}>
                      {generatedAssignmentLearningProfile.evidenceStrength} evidence
                    </span>
                  </div>
                  <h4>{generatedAssignmentLearningProfile.headline}</h4>
                  <p className='courseDetailsText'>{generatedAssignmentLearningProfile.learnerSnapshot}</p>
                  <div className='learningProfileHeroStats'>
                    {[
                      {
                        label: 'Average rubric',
                        value: generatedAssignmentLearningProfile.averageRubricPercent ? `${generatedAssignmentLearningProfile.averageRubricPercent}%` : '--',
                        note: 'Current review average'
                      },
                      {
                        label: 'Strongest signal',
                        value: generatedAssignmentLearningProfile.strengths[0]?.label || 'Still emerging',
                        note: 'Most stable recurring win'
                      },
                      {
                        label: 'Focus next',
                        value: generatedAssignmentLearningProfile.studentFacing.focusNext[0] || generatedAssignmentLearningProfile.developing[0]?.label || 'Keep building',
                        note: 'Best next lift'
                      },
                      {
                        label: 'Best mode',
                        value: generatedAssignmentLearningProfile.learningIdentity.primaryModes[0] || 'Still mapping',
                        note: 'How you learn fastest'
                      }
                    ].map((item) => (
                      <div className='learningProfileHeroStat' key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                        <small>{item.note}</small>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='learningProfileHeroAside'>
                  <strong>{generatedAssignmentLearningProfile.reviewCount}</strong>
                  <span>Reviewed assignments used</span>
                  <small>{generatedAssignmentLearningProfile.evidenceSummary || 'The profile updates itself when new reviewed work is available.'}</small>
                </div>
              </div>

              <div className='learningProfileMeta learningProfileMetaGenerated'>
                <div>
                  <span>Confidence signal</span>
                  <strong>{generatedAssignmentLearningProfile.confidence}</strong>
                  <p>{generatedAssignmentLearningProfile.confidenceReason || 'This confidence level reflects how consistent the review evidence is so far.'}</p>
                </div>
                <div>
                  <span>Latest evidence</span>
                  <strong>{generatedAssignmentLearningProfile.latestEvidenceLabel}</strong>
                  <p>This profile only rebuilds when your reviewed assignment history changes.</p>
                </div>
                <div>
                  <span>Last analysis</span>
                  <strong>{generatedAssignmentLearningProfile.lastAnalyzedLabel}</strong>
                  <p>Last analysis run using the newest reviewed assignment evidence available for your account.</p>
                </div>
              </div>

              <section className='learningProfileSection'>
                <div className='learningProfileSectionHeader'>
                  <div>
                    <span>Signal Map</span>
                    <h5>How your evidence is stacking up right now</h5>
                  </div>
                  <p>These charts compress your reviewed assignment history into fast-read trend signals.</p>
                </div>

                <div className='learningProfileChartGrid'>
                  <article className='studentAssessmentCard studentAssessmentCardWide learningProfileChartCard'>
                  <span>Rubric trend map</span>
                  <h5>How your scored dimensions are trending</h5>
                  {generatedAssignmentLearningProfile.rubricAverages.length > 0 ? (
                    <div className='learningProfileBarStack'>
                      {generatedAssignmentLearningProfile.rubricAverages.map((item) => (
                        <div key={`rubric-${item.label}`} className='learningProfileBarItem'>
                          <div className='learningProfileBarTop'>
                            <strong>{item.label}</strong>
                            <small>{item.averagePercent}% average</small>
                          </div>
                          <ProgressBar now={item.averagePercent} className='learningProfileProgress' />
                          <span>{item.count} reviewed assignment{item.count === 1 ? '' : 's'} contributed to this signal.</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>Rubric trend bars will appear here once scored dimensions are available in your assignment reviews.</p>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide learningProfileChartCard'>
                  <span>Learning mode fit</span>
                  <h5>Study formats that currently unlock understanding fastest</h5>
                  {generatedAssignmentLearningProfile.learningModes.length > 0 ? (
                    <div className='learningProfileBarStack'>
                      {generatedAssignmentLearningProfile.learningModes.map((item) => (
                        <div key={`fit-${item.mode}`} className='learningProfileBarItem'>
                          <div className='learningProfileBarTop'>
                            <strong>{item.mode}</strong>
                            <small>{item.fit || 'Building signal'}</small>
                          </div>
                          <ProgressBar now={item.score} className='learningProfileProgress learningProfileProgressFit' />
                          <span>{item.evidence || 'This fit will become more precise as more reviewed work is added.'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>The best-fit study modes will appear here after more evidence is gathered from your reviewed work.</p>
                  )}
                  </article>
                </div>
              </section>

              <div className='studentAssessmentGrid learningProfileGeneratedGrid'>
                <div className='learningProfileInlineSection'>
                  <span>Current Snapshot</span>
                  <h5>Where your profile is strongest, where it is stretching, and what changed most recently</h5>
                </div>

                <article className='studentAssessmentCard'>
                  <span>Average rubric score</span>
                  <h5>{generatedAssignmentLearningProfile.averageRubricPercent}%</h5>
                  <p>This is the current average across all scored rubric dimensions included in your reviewed assignments.</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Strongest recurring dimension</span>
                  <h5>{generatedAssignmentLearningProfile.strengths[0]?.label || 'Still emerging'}</h5>
                  <p>{generatedAssignmentLearningProfile.strengths[0]?.evidence || 'As more reviewed work comes in, your clearest recurring performance strength will show here.'}</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Developing dimension</span>
                  <h5>{generatedAssignmentLearningProfile.developing[0]?.label || 'Keep building'}</h5>
                  <p>{generatedAssignmentLearningProfile.developing[0]?.evidence || 'The next high-value growth dimension will appear here when your reviews show a stable pattern.'}</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Transfer pattern</span>
                  <h5>{generatedAssignmentLearningProfile.learningIdentity.transferPattern || 'Still being mapped'}</h5>
                  <p>This tells you how easily your understanding is carrying into new prompts and new assignments.</p>
                </article>

                {generatedAssignmentLearningProfile.fireflyRecommendation.shouldInviteLiveSession && (
                  <article className='studentAssessmentCard studentAssessmentCardWide'>
                    <span>Firefly recommendation</span>
                    <h5>Firefly wants to support you live on the next learning lift</h5>
                    <p className='courseDetailsText'>
                      {generatedAssignmentLearningProfile.fireflyRecommendation.studentSummary
                        || 'A live Firefly session is recommended so this concept can be explained in a way that fits how you learn best.'}
                    </p>
                    <div className='learningProfileInsightGrid'>
                      <div className='learningProfileInsightItem'>
                        <strong>Recommended window</strong>
                        <p>{generatedAssignmentLearningProfile.fireflyRecommendation.inviteWithin || 'As soon as possible'}</p>
                      </div>
                      <div className='learningProfileInsightItem'>
                        <strong>Suggested session length</strong>
                        <p>{generatedAssignmentLearningProfile.fireflyRecommendation.suggestedSessionLength || '45 minutes'}</p>
                      </div>
                    </div>
                    {generatedAssignmentLearningProfile.fireflyRecommendation.focusConcepts.length > 0 && (
                      <div className='learningProfilePatternList'>
                        {generatedAssignmentLearningProfile.fireflyRecommendation.focusConcepts.map((item) => (
                          <div key={`firefly-focus-${item.label}`} className='learningProfilePatternItem'>
                            <h6>{item.label}</h6>
                            <p>{item.evidence || item.action || 'This concept has enough signal to deserve focused teaching support.'}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                )}

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>What changed since the last update</span>
                  <h5>How your profile is moving</h5>
                  <p className='courseDetailsText'>{generatedAssignmentLearningProfile.changeSinceLastUpdate.summary || 'The next time your reviewed work changes, this section will show what improved, what appeared, and what needs closer attention.'}</p>
                  <div className='learningProfileInsightGrid'>
                    {[
                      ['New strengths', generatedAssignmentLearningProfile.changeSinceLastUpdate.newStrengths],
                      ['Improved signals', generatedAssignmentLearningProfile.changeSinceLastUpdate.improvedSignals],
                      ['New risks', generatedAssignmentLearningProfile.changeSinceLastUpdate.newRisks],
                      ['Declined signals', generatedAssignmentLearningProfile.changeSinceLastUpdate.declinedSignals]
                    ].map(([label, items]) => (
                      <div className='learningProfileInsightItem' key={label}>
                        <strong>{label}</strong>
                        {items.length > 0 ? (
                          <div className='learningProfileChipList'>
                            {items.map((item) => <span key={`${label}-${item}`}>{item}</span>)}
                          </div>
                        ) : (
                          <p>No major change was flagged here in the latest rebuild.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>What is going well</span>
                  <h5>Patterns worth protecting</h5>
                  {generatedAssignmentLearningProfile.studentFacing.whatIsGoingWell.length > 0 ? (
                    <div className='learningProfileInsightGrid'>
                      {generatedAssignmentLearningProfile.studentFacing.whatIsGoingWell.map((item) => (
                        <div className='learningProfileInsightItem' key={`good-${item}`}>
                          <strong>{item}</strong>
                          <p>Keep repeating this pattern. It is showing up clearly in your reviewed work.</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>As more reviewed assignments build up, your strongest recurring wins will appear here.</p>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Focus next</span>
                  <h5>The clearest next lift for your work</h5>
                  {generatedAssignmentLearningProfile.studentFacing.focusNext.length > 0 ? (
                    <div className='learningProfileInsightGrid'>
                      {generatedAssignmentLearningProfile.studentFacing.focusNext.map((item) => (
                        <div className='learningProfileInsightItem' key={`focus-${item}`}>
                          <strong>{item}</strong>
                          <p>Use this as your next checkpoint before you submit the next assignment.</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>The next focus area will show here once your reviewed feedback identifies a clear pattern.</p>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Performance pattern map</span>
                  <h5>Where your work is consistently strong and where it is still growing</h5>
                  <div className='learningProfilePatternColumns'>
                    <div className='learningProfilePatternBlock'>
                      <strong>Strongest recurring dimensions</strong>
                      {generatedAssignmentLearningProfile.strengths.length > 0 ? (
                        <div className='learningProfilePatternList'>
                          {generatedAssignmentLearningProfile.strengths.map((item) => (
                            <div key={`strength-${item.label}`} className='learningProfilePatternItem'>
                              <h6>{item.label}</h6>
                              <p>{item.evidence || 'This strength has repeated clearly across your reviewed work.'}</p>
                              {item.action && <small>{item.action}</small>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className='courseDetailsText'>The strongest recurring dimensions will show here once your review history gets deeper.</p>
                      )}
                    </div>

                    <div className='learningProfilePatternBlock'>
                      <strong>Developing dimensions</strong>
                      {generatedAssignmentLearningProfile.developing.length > 0 ? (
                        <div className='learningProfilePatternList'>
                          {generatedAssignmentLearningProfile.developing.map((item) => (
                            <div key={`developing-${item.label}`} className='learningProfilePatternItem'>
                              <h6>{item.label}</h6>
                              <p>{item.evidence || 'This growth area is showing up often enough to stay visible in your profile.'}</p>
                              {item.action && <small>{item.action}</small>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className='courseDetailsText'>The clearest development areas will appear here once the profile sees repeated evidence.</p>
                      )}
                    </div>
                  </div>
                </article>

                <div className='learningProfileInlineSection'>
                  <span>Learning Identity</span>
                  <h5>How you absorb work best, what habits help, and what patterns slow you down</h5>
                </div>

                <article className='studentAssessmentCard'>
                  <span>How you learn best</span>
                  <h5>{generatedAssignmentLearningProfile.learningIdentity.primaryModes[0] || 'Your best mode is still being confirmed'}</h5>
                  <div className='learningProfileSimpleList'>
                    {(generatedAssignmentLearningProfile.learningIdentity.primaryModes.length
                      ? generatedAssignmentLearningProfile.learningIdentity.primaryModes
                      : ['The profile is still gathering enough repeated evidence to rank your strongest learning modes.']).map((item) => (
                      <p key={`mode-${item}`}>{item}</p>
                    ))}
                  </div>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Strong habits</span>
                  <h5>{generatedAssignmentLearningProfile.learningIdentity.strongestHabits[0] || 'Still emerging'}</h5>
                  <div className='learningProfileSimpleList'>
                    {(generatedAssignmentLearningProfile.learningIdentity.strongestHabits.length
                      ? generatedAssignmentLearningProfile.learningIdentity.strongestHabits
                      : ['As more reviews arrive, this card will identify the habits that are helping you most.']).map((item) => (
                      <p key={`habit-${item}`}>{item}</p>
                    ))}
                  </div>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Friction patterns</span>
                  <h5>{generatedAssignmentLearningProfile.learningIdentity.frictionPatterns[0] || 'No recurring friction flagged yet'}</h5>
                  <div className='learningProfileSimpleList'>
                    {(generatedAssignmentLearningProfile.learningIdentity.frictionPatterns.length
                      ? generatedAssignmentLearningProfile.learningIdentity.frictionPatterns
                      : ['If the same blockers keep showing up in future reviews, they will be surfaced here.']).map((item) => (
                      <p key={`friction-${item}`}>{item}</p>
                    ))}
                  </div>
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Learning momentum</span>
                  <h5>How your work is moving over time</h5>
                  <div className='learningProfileMomentumGrid'>
                    <div className='learningProfileMomentumCard'>
                      <strong>Pace profile</strong>
                      <p>{generatedAssignmentLearningProfile.learningIdentity.paceProfile || 'Your pace pattern is still being confirmed from repeated review evidence.'}</p>
                    </div>
                    <div className='learningProfileMomentumCard'>
                      <strong>Confidence pattern</strong>
                      <p>{generatedAssignmentLearningProfile.learningIdentity.confidencePattern || 'Your confidence pattern will become clearer as more reviewed work builds up.'}</p>
                    </div>
                    <div className='learningProfileMomentumCard'>
                      <strong>Consistency pattern</strong>
                      <p>{generatedAssignmentLearningProfile.performancePatterns.consistencyPattern || 'Consistency across reviewed assignments is still being mapped.'}</p>
                    </div>
                    <div className='learningProfileMomentumCard'>
                      <strong>Revision pattern</strong>
                      <p>{generatedAssignmentLearningProfile.performancePatterns.revisionPattern || 'How revision changes your work quality will appear here once the evidence is richer.'}</p>
                    </div>
                    <div className='learningProfileMomentumCard learningProfileMomentumCardWide'>
                      <strong>Curriculum readiness</strong>
                      <p>{generatedAssignmentLearningProfile.performancePatterns.curriculumReadiness || 'Your readiness for harder work and more independent tasks is still being evaluated from reviewed assignments.'}</p>
                    </div>
                    <div className='learningProfileMomentumCard learningProfileMomentumCardWide'>
                      <strong>Future of learning lens</strong>
                      <p>{generatedAssignmentLearningProfile.futureOfLearningLens || generatedAssignmentLearningProfile.momentumNarrative || 'This section will explain how your current patterns connect to future independent, adaptive learning.'}</p>
                    </div>
                  </div>
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Persistence vs understanding</span>
                  <h5>How your profile separates misunderstanding from inconsistency</h5>
                  <div className='learningProfilePersistenceGrid'>
                    {[
                      ['Does not understand yet', generatedAssignmentLearningProfile.diagnosticLens.persistence.notUnderstoodYet],
                      ['Understands but lacks consistency', generatedAssignmentLearningProfile.diagnosticLens.persistence.inconsistentExecution],
                      ['Knows it but rushes or skips checks', generatedAssignmentLearningProfile.diagnosticLens.persistence.rushesOrSkipsChecks]
                    ].map(([label, items]) => (
                      <div key={label} className='learningProfilePersistenceCard'>
                        <div className='learningProfileBarTop'>
                          <strong>{label}</strong>
                          <small>{items.length} signal{items.length === 1 ? '' : 's'}</small>
                        </div>
                        <ProgressBar now={Math.min(100, items.length * 28)} className='learningProfileProgress learningProfileProgressPersistence' />
                        {items.length > 0 ? (
                          <div className='learningProfileChipList'>
                            {items.map((item) => <span key={`${label}-${item}`}>{item}</span>)}
                          </div>
                        ) : (
                          <p>No recurring signal was strong enough to be placed here yet.</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {generatedAssignmentLearningProfile.diagnosticLens.persistence.interpretation && (
                    <div className='learningProfileLongform'>
                      <p><strong>What this means:</strong> {generatedAssignmentLearningProfile.diagnosticLens.persistence.interpretation}</p>
                    </div>
                  )}
                </article>

                <div className='learningProfileInlineSection'>
                  <span>Support Plan</span>
                  <h5>What teaching support, practice moves, and review evidence should shape your next assignment cycle</h5>
                </div>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Support roadmap</span>
                  <h5>The top actions your next teaching support should focus on</h5>
                  <div className='learningProfileSupportGrid'>
                    <div className='learningProfileSupportBlock'>
                      <strong>Intervention priority</strong>
                      {generatedAssignmentLearningProfile.teacherGuidance.interventionPriorities.length > 0 ? (
                        <div className='learningProfilePriorityList'>
                          {generatedAssignmentLearningProfile.teacherGuidance.interventionPriorities.map((item) => (
                            <div key={`priority-${item.rank}-${item.label}`} className='learningProfilePriorityItem'>
                              <div className='learningProfileBarTop'>
                                <strong>{item.rank}. {item.label || `Priority ${item.rank}`}</strong>
                              </div>
                              <p>{item.whyItMatters || 'This is one of the highest-value teaching priorities currently showing up in your review history.'}</p>
                              {item.firstMove && <small>{item.firstMove}</small>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className='courseDetailsText'>Ranked teaching priorities will appear here once the generated profile includes enough repeated evidence.</p>
                      )}
                    </div>

                    <div className='learningProfileSupportBlock'>
                      <strong>Teacher moves by timeframe</strong>
                      <div className='learningProfileMoveGrid'>
                        <div className='learningProfileMoveItem'>
                          <h6>Next lesson</h6>
                          {generatedAssignmentLearningProfile.teacherGuidance.nextLessonMoves.length > 0 ? (
                            <div className='learningProfileChipList'>
                              {generatedAssignmentLearningProfile.teacherGuidance.nextLessonMoves.map((item) => <span key={`lesson-${item}`}>{item}</span>)}
                            </div>
                          ) : (
                            <p>No next-lesson move has been locked in yet.</p>
                          )}
                        </div>
                        <div className='learningProfileMoveItem'>
                          <h6>This week</h6>
                          {generatedAssignmentLearningProfile.teacherGuidance.thisWeekMoves.length > 0 ? (
                            <div className='learningProfileChipList'>
                              {generatedAssignmentLearningProfile.teacherGuidance.thisWeekMoves.map((item) => <span key={`week-${item}`}>{item}</span>)}
                            </div>
                          ) : (
                            <p>No week-level support move has been locked in yet.</p>
                          )}
                        </div>
                        <div className='learningProfileMoveItem'>
                          <h6>Next assessment cycle</h6>
                          {generatedAssignmentLearningProfile.teacherGuidance.nextAssessmentCycleMoves.length > 0 ? (
                            <div className='learningProfileChipList'>
                              {generatedAssignmentLearningProfile.teacherGuidance.nextAssessmentCycleMoves.map((item) => <span key={`cycle-${item}`}>{item}</span>)}
                            </div>
                          ) : (
                            <p>No next-cycle support move has been locked in yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Practice plan</span>
                  <h5>What to keep doing before your next assignment</h5>
                  <div className='learningProfilePlanGrid'>
                    <div className='learningProfilePlanBlock'>
                      <strong>Keep doing</strong>
                      <div className='learningProfileChipList'>
                        {(generatedAssignmentLearningProfile.actionPlan.keepDoing.length
                          ? generatedAssignmentLearningProfile.actionPlan.keepDoing
                          : ['A stronger practice routine will appear here as the profile matures.']).map((item) => (
                          <span key={`keep-${item}`}>{item}</span>
                        ))}
                      </div>
                    </div>
                    <div className='learningProfilePlanBlock'>
                      <strong>Focus now</strong>
                      <div className='learningProfileChipList'>
                        {(generatedAssignmentLearningProfile.actionPlan.focusNow.length
                          ? generatedAssignmentLearningProfile.actionPlan.focusNow
                          : ['The next high-value focus will appear here after the next reviewed assignment.']).map((item) => (
                          <span key={`focus-now-${item}`}>{item}</span>
                        ))}
                      </div>
                    </div>
                    <div className='learningProfilePlanBlock'>
                      <strong>Practice routine</strong>
                      <div className='learningProfileChipList'>
                        {(generatedAssignmentLearningProfile.actionPlan.practiceRoutine.length
                          ? generatedAssignmentLearningProfile.actionPlan.practiceRoutine
                          : ['Short, repeated practice routines will show up here once your feedback history is richer.']).map((item) => (
                          <span key={`routine-${item}`}>{item}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(generatedAssignmentLearningProfile.actionPlan.nextChallenge || generatedAssignmentLearningProfile.studentFacing.whyThisMatters) && (
                    <div className='learningProfileLongform'>
                      {generatedAssignmentLearningProfile.actionPlan.nextChallenge && (
                        <p><strong>Next challenge:</strong> {generatedAssignmentLearningProfile.actionPlan.nextChallenge}</p>
                      )}
                      {generatedAssignmentLearningProfile.studentFacing.whyThisMatters && (
                        <p><strong>Why this matters:</strong> {generatedAssignmentLearningProfile.studentFacing.whyThisMatters}</p>
                      )}
                    </div>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Misconceptions to watch</span>
                  <h5>Repeated patterns that need slower checking</h5>
                  {generatedAssignmentLearningProfile.diagnosticLens.misconceptionRecurrence.length > 0 ? (
                    <div className='learningProfileInsightGrid'>
                      {generatedAssignmentLearningProfile.diagnosticLens.misconceptionRecurrence.map((item) => (
                        <div className='learningProfileInsightItem' key={`misconception-${item.label}`}>
                          <strong>{item.label}</strong>
                          <p>{item.evidence || item.action || 'This pattern has appeared often enough to stay visible in your profile.'}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>Repeated misconceptions will show here only when the same misunderstanding appears in more than one reviewed assignment.</p>
                  )}
                </article>

                {generatedAssignmentLearningProfile.recentReviews.length > 0 && (
                  <article className='studentAssessmentCard studentAssessmentCardWide'>
                    <span>Recent review snapshots</span>
                    <h5>The latest reviewed assignments shaping your profile</h5>
                    <div className='learningProfileReviewList'>
                      {generatedAssignmentLearningProfile.recentReviews.map((review) => (
                        <div key={review.id} className='learningProfileReviewItem'>
                          <div className='learningProfileReviewTop'>
                            <strong>{review.title}</strong>
                            <span>{review.date}</span>
                          </div>
                          <h6>{review.headline}</h6>
                          <p>{review.detail || 'This reviewed assignment contributed to your current learning profile.'}</p>
                          {review.courseName && <small>{review.courseName}</small>}
                        </div>
                      ))}
                    </div>
                  </article>
                )}

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Review trend summary</span>
                  <h5>Signals still carried from your full reviewed-assignment history</h5>
                  <div className='learningProfilePatternColumns'>
                    <div className='learningProfilePatternBlock'>
                      <strong>Recurring wins from reviewed work</strong>
                      {learningProfile.winSignals.length > 0 ? (
                        <div className='learningProfilePatternList'>
                          {learningProfile.winSignals.map((item) => (
                            <div key={`legacy-win-${item.text}`} className='learningProfilePatternItem'>
                              <h6>{item.text}</h6>
                              <p>{item.count === 1 ? 'Mentioned in 1 review' : `Mentioned in ${item.count} reviews`}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className='courseDetailsText'>Recurring wins will appear here once more reviewed assignments are available.</p>
                      )}
                    </div>

                    <div className='learningProfilePatternBlock'>
                      <strong>Recurring growth themes</strong>
                      {learningProfile.focusSignals.length > 0 ? (
                        <div className='learningProfilePatternList'>
                          {learningProfile.focusSignals.map((item) => (
                            <div key={`legacy-focus-${item.text}`} className='learningProfilePatternItem'>
                              <h6>{item.text}</h6>
                              <p>{item.count === 1 ? 'Shows up in 1 review' : `Shows up in ${item.count} reviews`}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className='courseDetailsText'>Recurring growth themes will appear here once more reviewed assignments are available.</p>
                      )}
                    </div>
                  </div>
                </article>

                {(generatedAssignmentLearningProfile.studentFacing.encouragement || generatedAssignmentLearningProfile.studentFacing.reflectionPrompt) && (
                  <article className='studentAssessmentCard studentAssessmentCardWide'>
                    <span>Student-facing note</span>
                    <h5>What your learning profile wants you to hear right now</h5>
                    <div className='learningProfileLongform'>
                      {generatedAssignmentLearningProfile.studentFacing.encouragement && (
                        <p>{generatedAssignmentLearningProfile.studentFacing.encouragement}</p>
                      )}
                      {generatedAssignmentLearningProfile.studentFacing.reflectionPrompt && (
                        <p><strong>Reflection prompt:</strong> {generatedAssignmentLearningProfile.studentFacing.reflectionPrompt}</p>
                      )}
                    </div>
                  </article>
                )}
              </div>
            </div>
          ) : learningProfile.reviewedCount > 0 ? (
            <div className='studentAssessmentPanel learningProfilePanel'>
              <div className='studentAssessmentOverviewHeader'>
                <div>
                  <h4>{learningProfile.latestSignal}</h4>
                  <p className='courseDetailsText'>{learningProfile.latestSummary}</p>
                </div>
                <div className='studentAssessmentOverviewCount'>
                  {learningProfile.reviewedCount} of {assignmentStats.total} reviewed
                </div>
              </div>

              <div className='learningProfileMeta'>
                <div>
                  <span>Latest reviewed assignment</span>
                  <strong>{learningProfile.latestEntry?.assignment?.title || 'Reviewed assignment'}</strong>
                  <p>{learningProfile.latestEntry ? formatCalendarDate(learningProfile.latestEntry.reviewedAt) : 'Awaiting next review'}</p>
                </div>
                <div>
                  <span>Current headline</span>
                  <strong>{learningProfile.latestHeadline}</strong>
                  <p>Your latest review is shaping the snapshot above.</p>
                </div>
                <div>
                  <span>Review coverage</span>
                  <strong>{assignmentStats.total > 0 ? Math.round((learningProfile.reviewedCount / assignmentStats.total) * 100) : 0}% of course assignments</strong>
                  <p>{assignmentStats.pending + assignmentStats.revision} still need submission and {assignmentStats.submitted} are awaiting review.</p>
                </div>
              </div>

              <div className='studentAssessmentGrid'>
                <article className='studentAssessmentCard'>
                  <span>Reviewed assignments</span>
                  <h5>{learningProfile.reviewedCount} / {assignmentStats.total || 0}</h5>
                  <p>{learningProfile.reviewedCount === 1 ? 'One assignment has completed feedback.' : `${learningProfile.reviewedCount} assignments have completed feedback.`}</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Average rubric score</span>
                  <h5>{learningProfile.averageRubricPercent}%</h5>
                  <p>{learningProfile.rubricDimensionCount > 0
                    ? `Based on ${learningProfile.rubricDimensionCount} scored rubric dimensions across your reviewed work.`
                    : 'Rubric averages will appear here once scored dimensions are available.'}</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Strongest area</span>
                  <h5>{learningProfile.strongestRubric?.label || 'Still emerging'}</h5>
                  <p>{learningProfile.strongestRubric
                    ? `This dimension is averaging ${learningProfile.strongestRubric.averagePercent}% across your reviews.`
                    : 'As more rubric evidence builds up, this card will highlight your strongest pattern.'}</p>
                </article>

                <article className='studentAssessmentCard'>
                  <span>Best next focus</span>
                  <h5>{learningProfile.focusRubric?.label || 'Keep building consistency'}</h5>
                  <p>{learningProfile.focusRubric
                    ? `This is currently your lowest recurring rubric average at ${learningProfile.focusRubric.averagePercent}%.`
                    : 'The next focus area will show up here as more feedback is collected.'}</p>
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>What your reviews say you do well</span>
                  {learningProfile.winSignals.length > 0 ? (
                    <div className='learningProfileInsightGrid'>
                      {learningProfile.winSignals.map((item) => (
                        <div className='learningProfileInsightItem' key={`win-${item.text}`}>
                          <strong>{item.text}</strong>
                          <p>{item.count === 1 ? 'Mentioned in 1 review' : `Mentioned in ${item.count} reviews`}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>Positive trends will appear here once more reviewed feedback is available.</p>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>What to keep improving</span>
                  {learningProfile.focusSignals.length > 0 ? (
                    <div className='learningProfileInsightGrid'>
                      {learningProfile.focusSignals.map((item) => (
                        <div className='learningProfileInsightItem' key={`focus-${item.text}`}>
                          <strong>{item.text}</strong>
                          <p>{item.count === 1 ? 'Shows up in 1 review' : `Shows up in ${item.count} reviews`}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='courseDetailsText'>Growth themes will gather here as more assignments are reviewed.</p>
                  )}
                </article>

                <article className='studentAssessmentCard studentAssessmentCardWide'>
                  <span>Recent review snapshots</span>
                  <div className='learningProfileReviewList'>
                    {learningProfile.recentReviews.map((review) => (
                      <div className='learningProfileReviewItem' key={review.id}>
                        <div className='learningProfileReviewTop'>
                          <strong>{review.title}</strong>
                          <span>{review.date}</span>
                        </div>
                        <h6>{review.signal}</h6>
                        <p>{review.detail}</p>
                      </div>
                    ))}
                  </div>
                </article>
              </div>
            </div>
          ) : (
            <div className='studentAssessmentNotice learningProfileEmpty'>
              Your reviewed assignments will build a learning profile here. Once feedback starts coming in, you’ll see your strongest patterns, growth areas, rubric trends, and recent review snapshots in one place.
            </div>
          )}

          {displayNotes.length > 0 && (
            <div className='learningProfileNotesSection'>
              <div className='learningProfileNotesHeader'>
                <h6>Shared course guidance</h6>
                <span>{displayNotes.length} notes</span>
              </div>
              <div className='notesGrid'>
                {displayNotes.map((note) => (
                  <div className='noteCard' key={note.id || note.title || note.name}>
                    <div className='noteHeader'>
                      <span className={`noteTag noteTag--${note.tag || 'action'}`}>{note.title || note.name || 'Note'}</span>
                      <span className='noteDate'>{note.date || note.week || ''}</span>
                    </div>
                    <p className='courseDetailsText'>{note.body || note.note || note.description || ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CourseDetailsComp
